import {
  type ItemId,
  combinePerkEffects,
  decodeMask,
  encodeMask,
  getUnlock,
  starsForOutcome,
  type ActionId,
  type EncodedTerrain,
  type MatchEvent,
  type MatchPlayer,
  type MatchSettings,
  type PerkEffect,
  type PlayerInput,
  type PlayerOutcome,
  type RecruitKind,
  type Relation,
  type Wealth,
  type WeaponId,
  isPvpMode,
} from '@cratered/shared';
import {
  ALLY_HP, BOUNTIES, BUY_TICKS, FLOATIE_KNOCKOFF, MAX_BUY_COUNT, MAX_TEAM_SIZE, PRICES, RECRUIT_PRICES, ROUND_INCOME,
  ITEMS, STARTING_COINS, coinsFor, isPurchasable,
} from './economy';
import {
  SLIDE_ANGLE, STOP_H, VESSEL_NAMES, applyBoatSnap, boatContains, boatSnap, buildBoats, crossesStop, damageBoat, deckY, hitBoat, isVessel,
  needsRepair, panelCenterX, repairBoat, updateBoats, type Boat, type BoatHit, type BoatSnap,
} from './boats';
import { DROWN_DEPTH, buildTerrain, freeFloat, freeSpawn, type LevelDef } from './levels';
import { resolveLevel } from './maps';
import { spawnsForTeams } from './mapgen';
import { AIR_STEER, GRAVITY, JUMP_VX, JUMP_VY, MAX_FALL, SOFT_FALL, STEP_UP, UNIT_H, UNIT_HALF_W, WALK_SPEED } from './physics';
import { clamp, mulberry32 } from './rng';
import { Terrain, type BuildFill, type Carve } from './terrain';
import {
  ACTIONS, BOMBLET, CATAPULT_BOULDERS, CATAPULT_CUP_H, CATAPULT_REACH, DIG_BITES, DIG_RADIUS, DIG_STEP, MALLET_PANEL_DAMAGE, MEDKIT_HEAL,
  MINE_FUSE, MINE_TRIGGER, PLACE_REACH, PLATFORM_H, PLATFORM_W, PULL_SPEED, SANDBAG_GAP, SANDBAG_H, SANDBAG_W, STRIKE_GAP, STRIKE_REACH,
  TELEPORT_REACH, isWeapon, launchSpeed, startingAmmo, zeroAmmo, type ActionDef,
} from './weapons';

export const TICK_MS = 1000 / 60;
export { UNIT_H, UNIT_HALF_W, WALK_SPEED };
const HEAD_ZONE = 18;
export const STAMINA = 280;
const JUMP_COST = 50;
/** The beat between turns: long enough for the camera to pull out to the whole map and back. */
export const BETWEEN_TICKS = 130;
const MAX_ROUNDS = 40;

export type Pose = 'idle' | 'walk' | 'throw' | 'gun' | 'kick' | 'ragdoll' | 'dead';

export interface Unit {
  id: string;
  playerId: string;
  team: number;
  name: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  maxHp: number;
  facing: 1 | -1;
  alive: boolean;
  onGround: boolean;
  /** y where a voluntary fall started; null when not falling or when knocked. */
  fallStart: number | null;
  /** Flying after a hit: tumbles, takes impact damage, cannot act. */
  ragdoll: boolean;
  spin: number;
  /** Ticks left lying down after a ragdoll landing. */
  downTicks: number;
  moveDir: -1 | 0 | 1;
  walkPhase: number;
  pose: Pose;
  poseTicks: number;
  aim: number;
  hitFlash: number;
  lastHitShot: string | null;
  lastHitBy: string | null;
  lastHitTick: number;
  isBot: boolean;
  /** Bought in the buy phase (not one of the team's standard moles). */
  ally: boolean;
  /** Where a standard mole returns to when the next fight starts. */
  spawnX: number;
  spawnY: number;
  /** Index of the boat it is standing on, so it rides the deck as the boat lists. */
  boat: number | null;
  /**
   * Standing on a floatie: stationary, never falls on its own, but any real hit knocks it off
   * into whatever is below. Cleared if it ever lands on solid ground.
   */
  floatie: boolean;
  /**
   * Flying along a line: toward a grapple hook that bit (left is huge), or dragged toward a
   * harpooner for `left` more px. No gravity until it arrives, is blocked, or runs out of line.
   */
  pull: Pull | null;
  /** What the hand holds during a swing or wind-up (a bat, a mallet, a pike); null otherwise. */
  held: WeaponId | null;
  /** In the air off a catapult: tumbles like a ragdoll but lands without harm. A hit on the way makes it a real ragdoll. */
  flung: boolean;
  /** Brawl: the thing this mole has in hand, and ticks until it may use something again. */
  action: ActionId | null;
  cooldown: number;
  jumpCooldown: number;
}

export interface Pull {
  x: number;
  y: number;
  left: number;
}

/** A mine lying on the ground. Arms when the turn that planted it ends. */
export interface Mine {
  id: number;
  x: number;
  y: number;
  owner: string;
  team: number;
  shotId: string;
  originX: number;
  armed: boolean;
  /** Ticks until it goes off once set off; -1 while quiet. */
  fuse: number;
}

/** A catapult on the field: a map emplacement with a pile of boulders that refills every fight. */
export interface Catapult {
  id: number;
  x: number;
  y: number;
  dir: 1 | -1;
  boulders: number;
}

export interface Projectile {
  id: number;
  weapon: WeaponId;
  /** The mole that threw it (a hook pulls it; nothing else needs it). */
  unitId: string;
  /** One of a cluster bomb's bomblets: small, no ghost path. */
  frag?: boolean;
  /** A sticky bomb that has bitten: it stays put (or rides `stuckTo`) until its fuse runs out. */
  stuck?: boolean;
  stuckTo?: string | null;
  x: number;
  y: number;
  vx: number;
  vy: number;
  owner: string;
  team: number;
  shotId: string;
  originX: number;
  fuse: number;
  bounces: number;
  trail: string;
  alive: boolean;
  age: number;
  homing: boolean;
  power: number;
}

/** 'live' is Brawl's one playing phase: everyone acts at once until a side is gone. */
export type Phase = 'buy' | 'select' | 'move' | 'aim' | 'resolve' | 'between' | 'live' | 'ended';

/** Brawl: ticks between two uses of a thing. Guns are quick, lobs take a beat, the big stuff longer. */
export function cooldownFor(def: ActionDef): number {
  if (def.kind === 'melee') return 36;
  if (def.kind === 'hitscan') return def.id === 'gun' ? 40 : 70;
  if (def.kind === 'use') return 60;
  if (def.gear) return 75;
  if (def.id === 'ball') return 45;
  if (def.id === 'massive' || def.id === 'airstrike') return 180;
  return 90;
}
/** Brawl: a round's length before it is called a draw, and the beat between jumps. */
export const BRAWL_SECONDS = 180;
export const BRAWL_JUMP_TICKS = 22;

export interface TurnState {
  phase: Phase;
  teamIndex: number;
  unitId: string | null;
  playerId: string | null;
  ticksLeft: number;
  turnTicks: number;
  /** Turn cycles played (every team has had a turn). Quick-win par and sudden death count these. */
  round: number;
  /** Which fight of the match this is (1-based) and how many it is best of. */
  fight: number;
  bestOf: number;
  /** Fights won per team id. */
  wins: Record<number, number>;
  action: ActionId | null;
  stamina: number;
  moved: boolean;
  aimAngle: number;
  aimPower: number;
  wave: number;
}

export interface SimTeam {
  team: number;
  name: string;
  playerIds: string[];
  ammo: Record<WeaponId, number>;
  perks: PerkEffect;
  homingLeft: number;
  lastStand: boolean;
  revived: boolean;
  isEnemyAi: boolean;
  /** Which spawn / float lists this team uses (0 left, 1 right, 2 extras). */
  side: number;
  /** Shared team purse for the buy phase. */
  coins: number;
  /** Done shopping this buy phase. */
  ready: boolean;
  /** Attacks made per weapon, for the balance script. */
  used: Record<WeaponId, number>;
  /** Kit bought in the shop; kept for the whole match. */
  items: Record<ItemId, boolean>;
  /** Where this team's previous shot went, drawn while aiming for teams with the ghost. */
  lastShot: ShotGhost | null;
}

/** The path a shot took (every other tick for throws; muzzle and impact for bullets). */
export interface ShotGhost {
  weapon: WeaponId;
  path: [number, number][];
}

/** Transient things the renderer / HUD react to. Drained every tick. */
export type VisualEvent =
  | { kind: 'explosion'; x: number; y: number; r: number }
  | { kind: 'damage'; x: number; y: number; amount: number; team: number; headshot: boolean }
  | { kind: 'callout'; text: string }
  | { kind: 'death'; x: number; y: number; team: number; cause: 'damage' | 'drown' | 'fall' }
  | { kind: 'splash'; x: number; y: number }
  | { kind: 'shot'; x0: number; y0: number; x1: number; y1: number; weapon?: WeaponId }
  /** A grapple hook bit the ground here. */
  | { kind: 'hook'; x: number; y: number }
  /** Ground was built here (a plank, a wall, a mended panel). */
  | { kind: 'build'; x: number; y: number }
  | { kind: 'heal'; x: number; y: number; amount: number }
  /** A pickaxe bit the ground here. */
  | { kind: 'dig'; x: number; y: number; r: number }
  /** An airstrike was called on x: the plane crosses the top of the map this way. */
  | { kind: 'strike'; x: number; dir: 1 | -1 }
  /** A mole blinked from here to there. */
  | { kind: 'teleport'; x0: number; y0: number; x1: number; y1: number }
  /** A crossbow bolt stuck in the ground here, pointing this way. */
  | { kind: 'bolt'; x: number; y: number; angle: number }
  /** A sticky bomb bit here (onto a mole when `unitId` is set). */
  | { kind: 'stick'; x: number; y: number; unitId: string | null }
  /** A vessel blew up: a fireball as wide as the boat, centred here. */
  | { kind: 'boom'; x: number; y: number; w: number }
  /** This catapult just let fly. */
  | { kind: 'catapult'; id: number }
  | { kind: 'kick'; x: number; y: number; dir: 1 | -1; weapon?: WeaponId }
  | { kind: 'say'; unitId: string; text: string }
  | { kind: 'shake'; power: number }
  | { kind: 'wave'; wave: number }
  /** A boat panel broke apart. */
  | { kind: 'panel'; x: number; y: number }
  /** The field was reset for the next fight: graves, splashes, craters and particles go. */
  | { kind: 'reset' }
  | { kind: 'turn'; playerId: string | null; unitId: string | null };

export interface SimOptions {
  seed: number;
  settings: MatchSettings;
  players: MatchPlayer[];
  /** Co-op / campaign enemy configuration. */
  enemies?: { count: number; hp: number; difficulty: number; name?: string };
  campaignLevelId?: string;
  /** Override the round-1 budget (the balance script hands out unlimited coins). */
  startingCoins?: number;
}

/** Host bookkeeping the renderer never shows but a replacement host needs to carry on. */
export interface HostState {
  shotCounter: number;
  projectileCounter: number;
  mineCounter: number;
  settleTicks: number;
  wavesLeft: number;
  shotHitSomething: boolean;
  lastUnitByTeam: Record<string, string>;
  teams: { team: number; lastStand: boolean; revived: boolean; homingLeft: number; used: Record<WeaponId, number>; items: Record<ItemId, boolean> }[];
  winnerTeam: number | null;
  outcomes: PlayerOutcome[] | null;
  /** Buy phase: which team's turn starts when everyone is ready, and who is ready. */
  pendingTeamIndex: number;
  readyPlayers: string[];
  /** Fight bookkeeping: shop before the next turn, reset the field first, cycle the fight began on. */
  buyNext: boolean;
  resetPending: boolean;
  fightStartRound: number;
  forfeitedTeams: number[];
  fightFirstTeamIndex: number;
}

export interface Snapshot {
  tick: number;
  units: Unit[];
  projectiles: Projectile[];
  turn: TurnState;
  wind: number;
  carves: Carve[];
  visual: VisualEvent[];
  /** Per team; unlimited ammo travels as -1 because JSON has no Infinity. */
  ammo: Record<number, Record<WeaponId, number>>;
  coins: Record<number, number>;
  ready: Record<number, boolean>;
  /** Kit owned per team. */
  items: Record<number, Record<ItemId, boolean>>;
  /** Teams whose last shot changed since the previous snapshot (every team's in a sync). */
  ghosts: { team: number; ghost: ShotGhost }[];
  boats: BoatSnap[];
  mines: Mine[];
  catapults: Catapult[];
  /** Bumped whenever the field is rebuilt (new fight), so replicas restore their terrain mask. */
  terrainEpoch: number;
  ended: boolean;
  /** How many events the host has logged in total; `events` are the newest `events.length` of them. */
  eventsTotal: number;
  events: MatchEvent[];
  host: HostState;
}

/** The whole match for a client that joins mid-match, or that takes over as host. */
export interface SimSync {
  terrain: EncodedTerrain;
  /** The complete event log at the time of the sync. */
  events: MatchEvent[];
  snapshot: Snapshot;
}

/** Whose blast this is, so a vessel it sinks can credit them. */
interface BlastCredit {
  owner: string;
  team: number;
  shotId: string;
  originX: number;
}

/** What a vessel does when its last hit point goes. */
const VESSEL_BLAST: ActionDef = {
  id: 'grenade', name: 'Explosion', short: 'BOOM', kind: 'throw', ammo: 0, damage: 45, radius: 150, knockback: 13, speed: 0, gravity: 0, hint: '',
};

interface SimRestore {
  snapshot: Snapshot;
  events: MatchEvent[];
  terrain: Terrain;
}

const UNLIMITED = -1;

/**
 * Where a platform goes for an aim (angle, power): along the aim from the mole's shoulder, up to
 * PLACE_REACH px, so with the pointer the plank sits under the cursor. A spot in the water floats
 * on the surface. `ok` is false when the plank would cut through someone's body (feet inside it
 * are fine: they end up standing on it).
 */
export function platformSpot(
  level: LevelDef, units: readonly Unit[], unit: Unit, angle: number, power: number, w = PLATFORM_W,
): { x: number; y: number; ok: boolean } {
  const dirX = Math.cos(angle) * unit.facing;
  const dirY = -Math.sin(angle);
  const reach = PLACE_REACH * clamp(power, 0, 1);
  const x = clamp(unit.x + dirX * reach, w / 2, level.width - w / 2);
  let y = clamp(unit.y - UNIT_H * 0.7 + dirY * reach, PLATFORM_H, level.height - PLATFORM_H);
  if (level.waterY !== null && y > level.waterY - PLATFORM_H / 2) y = level.waterY + 4;
  let ok = true;
  for (const u of units) {
    if (!u.alive || Math.abs(u.x - x) > w / 2 + UNIT_HALF_W * 0.8) continue;
    const top = y - PLATFORM_H / 2;
    const bottom = y + PLATFORM_H / 2;
    if (bottom > u.y - UNIT_H + 4 && top < u.y - 6) ok = false;
  }
  return { x, y, ok };
}

/**
 * Where a teleport puts the mole for an aim: its feet land along the aim from its middle, up to
 * TELEPORT_REACH px. `ok` is false when the body would be inside solid ground, off the map, or
 * below the drowning / fall line.
 */
export function teleportSpot(
  level: LevelDef, isSolid: (x: number, y: number) => boolean, unit: Unit, angle: number, power: number,
): { x: number; y: number; ok: boolean } {
  const dirX = Math.cos(angle) * unit.facing;
  const dirY = -Math.sin(angle);
  const reach = TELEPORT_REACH * clamp(power, 0, 1);
  const x = clamp(unit.x + dirX * reach, UNIT_HALF_W + 2, level.width - UNIT_HALF_W - 2);
  const y = clamp(unit.y + dirY * reach, UNIT_H + 4, level.height - 10);
  let ok = true;
  const deathY = level.waterY !== null ? level.waterY : level.height;
  if (y > deathY - 2) ok = false;
  for (let dy = 2; dy < UNIT_H && ok; dy += 6) {
    if (isSolid(x - UNIT_HALF_W * 0.6, y - dy) || isSolid(x + UNIT_HALF_W * 0.6, y - dy) || isSolid(x, y - dy)) ok = false;
  }
  return { x, y, ok };
}

/** The ground x an airstrike is called on: the drag's horizontal reach, up to STRIKE_REACH, the way the mole faces. */
export function strikeSpot(level: LevelDef, unit: Unit, angle: number, power: number): number {
  const out = Math.max(0, Math.cos(angle)) * clamp(power, 0, 1) * STRIKE_REACH;
  return clamp(unit.x + unit.facing * out, 60, level.width - 60);
}

/** The catapult a mole is standing beside with boulders left, if any. */
export function catapultNear(catapults: readonly Catapult[], unit: Unit): Catapult | null {
  for (const c of catapults) {
    if (c.boulders > 0 && Math.abs(unit.x - c.x) <= CATAPULT_REACH && Math.abs(unit.y - c.y) <= 80) return c;
  }
  return null;
}

function packAmmo(a: Record<WeaponId, number>): Record<WeaponId, number> {
  const out = { ...a };
  for (const k of Object.keys(out) as WeaponId[]) if (out[k] === Infinity) out[k] = UNLIMITED;
  return out;
}

function unpackAmmo(a: Record<WeaponId, number>): Record<WeaponId, number> {
  const out = { ...a };
  for (const k of Object.keys(out) as WeaponId[]) {
    const v = out[k];
    if (v === UNLIMITED || typeof v !== 'number') out[k] = Infinity;
  }
  return out;
}

/** What the renderer and HUD read. Both the host's Sim and the remote replica satisfy it. */
export interface ViewState {
  readonly level: LevelDef;
  readonly terrain: Terrain;
  readonly boats: readonly Boat[];
  readonly mines: readonly Mine[];
  readonly catapults: readonly Catapult[];
  readonly units: readonly Unit[];
  readonly projectiles: readonly Projectile[];
  readonly turn: TurnState;
  readonly wind: number;
  readonly teams: readonly SimTeam[];
  readonly ended: boolean;
  readonly tick: number;
  drainVisual(): VisualEvent[];
  trailFor(playerId: string): string;
}

const OUCH = ['Ow!', 'Hey!', 'Rude.', 'My fur!', 'Not the face!', 'Oof.'];
const KICKED = ['Wheee—', 'Nooo', 'Unfair!', 'AAAA'];
const BYE = ['Bye!', 'Enjoy the swim.', 'Mind the gap.', 'Later!'];
const WHIFF = ['Whiff.', 'Warming up.', 'That was a test.'];

export class Sim implements ViewState {
  tick = 0;
  readonly level: LevelDef;
  readonly terrain: Terrain;
  units: Unit[] = [];
  projectiles: Projectile[] = [];
  teams: SimTeam[] = [];
  turn: TurnState;
  wind = 0;
  ended = false;
  winnerTeam: number | null = null;
  outcomes: PlayerOutcome[] | null = null;
  readonly events: MatchEvent[] = [];
  readonly settings: MatchSettings;
  readonly players: MatchPlayer[];
  /** How much of `events` has gone out in snapshots. */
  private eventsSent = 0;
  private visual: VisualEvent[] = [];
  private carves: Carve[] = [];
  /** Positions of in-flight shots that started this match, by projectile id, for the last-shot ghost. */
  private shotPaths = new Map<number, [number, number][]>();
  private ghosts: { team: number; ghost: ShotGhost }[] = [];
  private rng: () => number;
  private shotCounter = 0;
  private projectileCounter = 0;
  private settleTicks = 0;
  private autoplay = new Set<string>();
  private trails = new Map<string, string>();
  private lastUnitByTeam = new Map<number, string>();
  private readonly enemyCfg: SimOptions['enemies'];
  private readonly campaignLevelId?: string;
  private readonly startingCoins: number;
  private readonly wealth: Wealth;
  private wavesLeft: number;
  private shotHitSomething = false;
  private pendingTeamIndex = -1;
  private readyPlayers = new Set<string>();
  /** The next turn opens with the shop (match start, after a fight, before a co-op wave). */
  private buyNext = true;
  /** A fight just ended; respawn everyone before the next shop. */
  private resetPending = false;
  private fightStartRound = 0;
  private forfeitedTeams = new Set<number>();
  /** Versus: the coin flip picks who opens each fight; a turn cycle completes when play returns to them. */
  private fightFirstTeamIndex = 0;
  boats: Boat[] = [];
  mines: Mine[] = [];
  catapults: Catapult[] = [];
  private mineCounter = 0;
  private terrainEpoch = 0;

  constructor(opts: SimOptions, restore?: SimRestore) {
    this.settings = opts.settings;
    this.players = opts.players;
    this.enemyCfg = opts.enemies;
    this.campaignLevelId = opts.campaignLevelId;
    this.startingCoins = opts.startingCoins ?? STARTING_COINS;
    // Upper class and broke apply to Versus and Co-op; the campaign always pays normal money.
    this.wealth = opts.settings.mode === 'campaign' ? 'normal' : opts.settings.wealth ?? 'normal';
    // A restored sim cannot recover the old host's RNG position; any fresh stream will do, the sim is authoritative.
    this.rng = mulberry32(restore ? (opts.seed ^ restore.snapshot.tick) >>> 0 : opts.seed);
    this.level = resolveLevel(opts.settings.map);
    this.terrain = restore?.terrain ?? buildTerrain(this.level);
    this.boats = buildBoats(this.level.boats);
    this.catapults = this.makeCatapults();
    this.wavesLeft = opts.settings.mode === 'coop' ? Math.max(1, opts.settings.waves) : 1;
    this.turn = {
      phase: 'between', teamIndex: -1, unitId: null, playerId: null, ticksLeft: 40,
      turnTicks: opts.settings.turnSeconds * 60, round: 0, fight: 1,
      bestOf: opts.settings.mode === 'campaign' ? 1 : opts.settings.bestOf ?? 3, wins: {},
      action: null, stamina: STAMINA, moved: false, aimAngle: 0.6, aimPower: 0.6, wave: 1,
    };
    for (const p of opts.players) this.trails.set(p.id, p.equipped.trail);
    if (restore) {
      this.restore(restore);
    } else {
      this.setupTeams();
      this.rollWind();
    }
  }

  /**
   * Continues a match from what a replica collected (its latest snapshot, event log and terrain):
   * how a client takes over when the host leaves mid-match. The terrain instance is adopted as is.
   */
  static fromSnapshot(opts: SimOptions, snapshot: Snapshot, events: MatchEvent[], terrain: Terrain): Sim {
    return new Sim(opts, { snapshot, events, terrain });
  }

  // ---------- setup ----------

  /** The player teams as the rules define them, without any moles yet. */
  private buildTeams(): { team: SimTeam; members: MatchPlayer[] }[] {
    const mode = this.settings.mode;
    const pvp = isPvpMode(mode);
    const teamIds = pvp ? [...new Set(this.players.map((p) => p.team))].sort() : [0];
    return teamIds.map((teamId, index) => {
      const members = this.players.filter((p) => (pvp ? p.team === teamId : true));
      const side = pvp ? Math.min(2, (index % 2 === 0 ? 0 : 1) + (index >= 2 ? 2 : 0)) : 0;
      const perks = pvp
        ? {}
        : combinePerkEffects(members.flatMap((m) => m.perks.map((id) => getUnlock(id)!).filter(Boolean)));
      const team: SimTeam = {
        team: teamId,
        name: pvp ? `Team ${teamId + 1}` : 'Moles',
        playerIds: members.map((m) => m.id),
        ammo: startingAmmo(),
        perks,
        homingLeft: perks.homingRockets ?? 0,
        lastStand: false,
        revived: false,
        isEnemyAi: false,
        side,
        coins: 0,
        ready: false,
        used: zeroAmmo(),
        items: { ghost: false },
        lastShot: null,
      };
      if (perks.extraAmmo) {
        for (const [w, n] of Object.entries(perks.extraAmmo) as [WeaponId, number][]) team.ammo[w] = (team.ammo[w] ?? 0) + n;
      }
      return { team, members };
    });
  }

  private buildEnemyTeam(): SimTeam {
    return {
      team: 1, name: this.enemyCfg?.name ?? 'Raiders', playerIds: ['ai'], ammo: startingAmmo(), perks: {},
      homingLeft: 0, lastStand: false, revived: false, isEnemyAi: true, side: 1, coins: 0, ready: false, used: zeroAmmo(),
      items: { ghost: false }, lastShot: null,
    };
  }

  private setupTeams(): void {
    const mode = this.settings.mode;
    const built = this.buildTeams();
    const used = new Set<string>();
    // Each team's own patch of the map (the authored sides for two or three teams).
    const perTeam = mode === 'brawl' ? Math.max(1, ...built.map((b) => b.members.length)) : Math.max(this.settings.unitsPerTeam, ...built.map((b) => b.members.length));
    const plan = isPvpMode(mode) ? spawnsForTeams(this.level, built.length, Math.min(4, perTeam)) : this.level.spawns;
    const spawnFor = (side: number): [number, number] => {
      const lists = [plan[side] ?? [], ...plan, ...this.level.spawns];
      for (const list of lists) {
        for (const s of list) {
          const key = `${s[0]},${s[1]}`;
          if (!used.has(key)) {
            used.add(key);
            return s;
          }
        }
      }
      return [this.level.width / 2, 0];
    };

    built.forEach(({ team, members }, index) => {
      const teamId = team.team;
      const perks = team.perks;
      this.teams.push(team);

      const perMember = mode === 'coop' ? 2 : this.settings.unitsPerTeam;
      // Brawl: one mole per player, every hand on its own keys.
      const total = mode === 'brawl' ? members.length : mode === 'versus' ? Math.max(this.settings.unitsPerTeam, members.length) : perMember * members.length;
      for (let i = 0; i < total; i++) {
        const owner = members[i % members.length];
        const [sx, sy] = spawnFor(isPvpMode(mode) ? index : team.side);
        const maxHp = 100 + (perks.maxHpBonus ?? 0);
        const label = total > 1 ? `${owner.name} ${i + 1}` : owner.name;
        this.units.push(this.makeUnit(`${owner.id}#${i}`, owner.id, teamId, label, sx, this.findGround(sx, sy), maxHp, false, sx < this.level.width / 2 ? 1 : -1));
      }
    });

    if (!isPvpMode(mode)) {
      this.teams.push(this.buildEnemyTeam());
      this.spawnEnemies(spawnFor);
    }
  }

  private restore(r: SimRestore): void {
    const s = r.snapshot;
    for (const { team } of this.buildTeams()) this.teams.push(team);
    if (!isPvpMode(this.settings.mode)) this.teams.push(this.buildEnemyTeam());
    this.tick = s.tick;
    this.units = structuredClone(s.units);
    // Nobody simulated while the host was away; a key held back then may long be released.
    for (const u of this.units) u.moveDir = 0;
    this.projectiles = structuredClone(s.projectiles);
    this.turn = structuredClone(s.turn);
    this.wind = s.wind;
    this.ended = s.ended;
    for (const t of this.teams) {
      const ammo = s.ammo[t.team];
      if (ammo) t.ammo = unpackAmmo(ammo);
      t.coins = s.coins?.[t.team] ?? 0;
      t.ready = s.ready?.[t.team] ?? false;
      const h = s.host.teams.find((x) => x.team === t.team);
      if (!h) continue;
      t.lastStand = h.lastStand;
      t.revived = h.revived;
      t.homingLeft = h.homingLeft;
      t.used = { ...zeroAmmo(), ...(h.used ?? {}) };
      t.items = h.items ? { ...h.items } : { ghost: false };
    }
    for (const g of s.ghosts ?? []) {
      const t = this.teamOf(g.team);
      if (t) t.lastShot = g.ghost;
    }
    for (const bs of s.boats ?? []) {
      const b = this.boats.find((x) => x.id === bs.id);
      if (b) applyBoatSnap(b, bs);
    }
    this.mines = structuredClone(s.mines ?? []);
    if (s.catapults) this.catapults = structuredClone(s.catapults);
    this.mineCounter = s.host.mineCounter ?? 0;
    this.terrainEpoch = s.terrainEpoch ?? 0;
    this.pendingTeamIndex = s.host.pendingTeamIndex ?? -1;
    this.readyPlayers = new Set(s.host.readyPlayers ?? []);
    this.buyNext = s.host.buyNext ?? false;
    this.resetPending = s.host.resetPending ?? false;
    this.fightStartRound = s.host.fightStartRound ?? 0;
    this.forfeitedTeams = new Set(s.host.forfeitedTeams ?? []);
    this.fightFirstTeamIndex = s.host.fightFirstTeamIndex ?? 0;
    for (const e of r.events) this.events.push(e);
    this.eventsSent = this.events.length;
    this.shotCounter = s.host.shotCounter;
    this.projectileCounter = s.host.projectileCounter;
    this.settleTicks = s.host.settleTicks;
    this.wavesLeft = s.host.wavesLeft;
    this.shotHitSomething = s.host.shotHitSomething;
    this.lastUnitByTeam = new Map(Object.entries(s.host.lastUnitByTeam).map(([team, unitId]) => [Number(team), unitId]));
    this.winnerTeam = s.host.winnerTeam;
    this.outcomes = s.host.outcomes ? structuredClone(s.host.outcomes) : null;
  }

  private spawnEnemies(spawnFor?: (side: number) => [number, number]): void {
    const cfg = this.enemyCfg ?? { count: 3, hp: 80, difficulty: 0.5 };
    const wave = this.turn.wave;
    const count = this.settings.mode === 'coop' ? cfg.count + (wave - 1) : cfg.count;
    const hp = this.settings.mode === 'coop' ? cfg.hp + (wave - 1) * 10 : cfg.hp;
    const names = ['Grit', 'Rubble', 'Shale', 'Flint', 'Soot', 'Clod', 'Pebble', 'Mud', 'Dusty', 'Gravel', 'Ash', 'Crag'];
    const pool = [...(this.level.spawns[1] ?? []), ...(this.level.spawns[2] ?? [])];
    for (let i = 0; i < count; i++) {
      let spot: [number, number];
      if (spawnFor) spot = spawnFor(1);
      else {
        const free = pool.filter((s) => !this.units.some((u) => u.alive && Math.abs(u.x - s[0]) < 50 && Math.abs(u.y - s[1]) < 80));
        spot = (free.length ? free : pool)[(i + wave) % (free.length ? free.length : pool.length)] ?? [this.level.width * 0.75, 0];
      }
      this.units.push(this.makeUnit(`ai#${wave}-${i}`, 'ai', 1, names[(i + wave * 3) % names.length], spot[0], this.findGround(spot[0], spot[1]), hp, true, -1));
    }
  }

  /** The level's catapults with full boulder piles. */
  private makeCatapults(): Catapult[] {
    return (this.level.catapults ?? []).map((c, i) => ({ id: i + 1, x: c.x, y: c.y, dir: c.dir, boulders: CATAPULT_BOULDERS }));
  }

  /** The catapult this mole could wind right now (beside it, boulders left). */
  catapultFor(unit: Unit): Catapult | null {
    return catapultNear(this.catapults, unit);
  }

  private makeUnit(id: string, playerId: string, team: number, name: string, x: number, y: number, maxHp: number, isBot: boolean, facing: 1 | -1): Unit {
    return {
      id, playerId, team, name, x, y, vx: 0, vy: 0, hp: maxHp, maxHp, facing, alive: true, onGround: false,
      fallStart: null, ragdoll: false, spin: 0, downTicks: 0, moveDir: 0, walkPhase: 0, pose: 'idle', poseTicks: 0,
      aim: 0.6, hitFlash: 0, lastHitShot: null, lastHitBy: null, lastHitTick: -9999, isBot, ally: false, floatie: false,
      spawnX: x, spawnY: y, boat: null, pull: null, held: null, flung: false, action: null, cooldown: 0, jumpCooldown: 0,
    };
  }

  private rollWind(): void {
    this.wind = this.settings.wind ? clamp((this.rng() * 2 - 1) * (0.4 + this.rng() * 0.6), -1, 1) : 0;
  }

  /** Mark a player as away so the host's bot plays their turns. */
  setAutoplay(playerId: string, on: boolean): void {
    if (on) this.autoplay.add(playerId);
    else this.autoplay.delete(playerId);
  }

  isBotTurn(): boolean {
    if (this.settings.mode === 'brawl') return this.turn.phase === 'live' && this.autoplay.size > 0;
    const u = this.activeUnit();
    return !!u && (u.isBot || this.autoplay.has(u.playerId));
  }

  /** Brawl: the players the host's bot drives (away players), with their living moles. */
  botUnits(): Unit[] {
    return this.units.filter((u) => u.alive && this.autoplay.has(u.playerId));
  }

  /** The host's bot shops and plays for this team: raiders, or a team whose players are all away. */
  isBotControlled(team: SimTeam): boolean {
    return team.isEnemyAi || (team.playerIds.length > 0 && team.playerIds.every((id) => this.autoplay.has(id)));
  }

  teamOfPlayer(playerId: string): SimTeam | undefined {
    return this.teams.find((t) => t.playerIds.includes(playerId));
  }

  /** Teams that still have moles on the field. */
  liveTeams(): SimTeam[] {
    return this.teams.filter((team) => this.units.some((u) => u.alive && u.team === team.team));
  }

  isPlayerReady(playerId: string): boolean {
    return this.readyPlayers.has(playerId);
  }

  trailFor(playerId: string): string {
    return this.trails.get(playerId) ?? 'trail.none';
  }

  drainVisual(): VisualEvent[] {
    const v = this.visual;
    this.visual = [];
    return v;
  }

  activeUnit(): Unit | null {
    return this.units.find((u) => u.id === this.turn.unitId && u.alive) ?? null;
  }

  teamOf(id: number): SimTeam | undefined {
    return this.teams.find((t) => t.team === id);
  }

  /** Where a mole is out: DROWN_DEPTH under the water's surface, or below the bottom of a pit map. */
  get deathY(): number {
    return this.level.waterY !== null ? this.level.waterY + DROWN_DEPTH : this.level.height + 40;
  }

  /** Solid ground: the terrain mask or an intact boat hull. */
  isSolid(x: number, y: number): boolean {
    if (this.terrain.isSolid(x, y)) return true;
    for (const b of this.boats) if (boatContains(b, x, y)) return true;
    return false;
  }

  private boatIndexAt(x: number, y: number): number | null {
    for (let i = 0; i < this.boats.length; i++) if (boatContains(this.boats[i], x, y)) return i;
    return null;
  }

  /** Y to stand at when dropped onto column x near y, counting boats. */
  findGround(x: number, y: number): number {
    for (let yy = Math.max(0, Math.floor(y - 160)); yy < this.level.height; yy++) {
      if (this.isSolid(x, yy)) return yy - 1;
    }
    return this.level.height - 1;
  }

  /** Dig a crater by hand (level scripting, tests); replicas hear about it like any blast. */
  carveAt(x: number, y: number, r: number): void {
    this.carve(x, y, r);
  }

  private carve(x: number, y: number, r: number): void {
    if (r <= 0) return;
    this.terrain.carve(x, y, r);
    this.carves.push({ x, y, r });
  }

  /**
   * Blasts dig craters in the ground and hurt boats: a raft loses deck panels and pontoon lift and
   * lists or sinks; a vessel wears down and blows up on its last point. `credit` says who gets the
   * kills when a vessel goes up.
   */
  private blastTerrain(x: number, y: number, radius: number, dmg: number, credit?: BlastCredit): void {
    this.carve(x, y, radius * 0.55);
    for (const b of this.boats) {
      if (b.sunk) continue;
      const hit = hitBoat(b, x, y, radius, dmg);
      if (hit.touched) this.afterBoatHit(b, hit, credit);
    }
  }

  /** What a hit on a boat leaves behind: splinters, callouts, and for a vessel the explosion. */
  private afterBoatHit(b: Boat, hit: BoatHit, credit?: BlastCredit): void {
    const sy = b.y + b.sink;
    if (!isVessel(b)) {
      for (const i of hit.broke) this.visual.push({ kind: 'panel', x: panelCenterX(b, i), y: sy });
      for (const i of hit.floatsBroke) this.visual.push({ kind: 'panel', x: panelCenterX(b, i), y: sy + b.hull - 10 });
    }
    if (isVessel(b) && !b.parentId) {
      if (hit.sank) this.vesselBlows(b, credit);
      return;
    }
    if (b.sunk) this.visual.push({ kind: 'callout', text: 'Going down!' });
    else if (!b.listed && Math.abs(b.angleTarget) > 0.16) {
      b.listed = true;
      this.visual.push({ kind: 'callout', text: "She's listing!" });
    }
  }

  /** A vessel lost its last hit point: it goes up in a fireball, hurts whoever is near, and sinks. */
  private vesselBlows(b: Boat, credit?: BlastCredit): void {
    const kind = b.kind as keyof typeof VESSEL_NAMES;
    this.visual.push({ kind: 'callout', text: `${VESSEL_NAMES[kind]} blows up!` });
    const cx = b.x;
    const cy = b.y + b.sink - 14;
    const c = credit ?? { owner: 'ai', team: -1, shotId: `boat:${this.tick}`, originX: cx };
    this.visual.push({ kind: 'boom', x: cx, y: cy, w: b.panelW * b.panels.length });
    this.blast(cx, cy, VESSEL_BLAST, 'grenade', c.owner, c.team, c.shotId, c.originX, 1, null);
  }

  /** A bullet that stops in a hull wears it a little. */
  private chipBoats(x: number, y: number, dmg: number, unit: Unit, shotId: string): void {
    for (const b of this.boats) {
      if (b.sunk || !boatContains(b, x, y)) continue;
      const hit = hitBoat(b, x, y, 14, dmg);
      if (hit.touched) this.afterBoatHit(b, hit, { owner: unit.playerId, team: unit.team, shotId, originX: unit.x });
    }
  }

  /** Enemy an active unit could kick right now, if any. */
  bootTarget(unit: Unit): Unit | null {
    return this.meleeTarget(unit, ACTIONS.boot.reach ?? 64);
  }

  /** Nearest enemy within `reach` px sideways (and roughly level), for any swing. */
  meleeTarget(unit: Unit, reach: number): Unit | null {
    let best: Unit | null = null;
    let bestD = Infinity;
    for (const u of this.units) {
      if (!u.alive || u === unit || u.team === unit.team) continue;
      const dx = u.x - unit.x;
      const dy = Math.abs(u.y - unit.y);
      if (Math.abs(dx) <= reach && dy <= 50 && Math.abs(dx) < bestD) {
        bestD = Math.abs(dx);
        best = u;
      }
    }
    return best;
  }

  // ---------- input ----------

  applyInput(playerId: string, input: PlayerInput, fromBot = false): void {
    if (this.ended) return;
    const t = this.turn;
    if (t.phase === 'buy') {
      if (input.kind === 'buy' || input.kind === 'ready' || input.kind === 'recruit' || input.kind === 'buyItem') this.applyBuyInput(playerId, input, fromBot);
      return;
    }
    if (input.kind === 'buy' || input.kind === 'ready' || input.kind === 'recruit') return;
    if (this.settings.mode === 'brawl') {
      if (t.phase === 'live' && (!fromBot || this.autoplay.has(playerId))) this.applyLiveInput(playerId, input);
      return;
    }
    if (t.playerId !== playerId && !(fromBot && this.isBotTurn())) return;
    const unit = this.activeUnit();
    if (!unit) return;
    const team = this.teamOf(unit.team);
    if (!team) return;

    switch (input.kind) {
      case 'select': {
        if (t.phase !== 'select' || t.moved) break;
        const next = this.units.find((u) => u.id === input.unitId && u.alive && u.team === unit.team && u.playerId === unit.playerId);
        if (next && next.id !== unit.id) {
          t.unitId = next.id;
          this.visual.push({ kind: 'turn', playerId: next.playerId, unitId: next.id });
        }
        break;
      }
      case 'action': {
        if (t.phase !== 'select') break;
        const a = input.action;
        if (a === 'move') {
          if (!t.moved && !unit.floatie) t.phase = 'move';
        } else if (a === 'skip') {
          this.endTurn(unit);
        } else if (isWeapon(a)) {
          if ((team.ammo[a] ?? 0) <= 0) break;
          const def = ACTIONS[a];
          if (def.pveOnly && isPvpMode(this.settings.mode)) break;
          // Gear takes the move: once you have walked (or used other gear) it is attacks only.
          if (def.gear && t.moved) break;
          // A catapult is wound from beside it, and only while its pile lasts.
          if (def.station && !this.catapultFor(unit)) break;
          if (def.kind === 'melee') {
            this.melee(unit, team, a);
          } else if (def.kind === 'use') {
            this.useGear(unit, team, a);
          } else {
            t.action = a;
            t.phase = 'aim';
            t.aimAngle = unit.aim;
            t.aimPower = def.fixedPower ? 1 : 0.6;
          }
        }
        break;
      }
      case 'stopMove':
        // Not while a hook is out or the mole is on the line: that move ends when it lands.
        if (t.phase === 'move' && !unit.pull && !this.projectiles.some((p) => p.alive)) {
          unit.moveDir = 0;
          t.phase = 'select';
          t.moved = true;
        }
        break;
      case 'cancel':
        if (t.phase === 'aim') {
          t.phase = 'select';
          t.action = null;
        }
        break;
      case 'move':
        if (t.phase === 'move' && !unit.ragdoll && !unit.pull) {
          unit.moveDir = input.dir;
          if (input.dir !== 0) unit.facing = input.dir;
        } else if (t.phase === 'select' || t.phase === 'aim') {
          // Turning around is free.
          if (input.dir !== 0) unit.facing = input.dir;
        }
        break;
      case 'jump':
        if (t.phase === 'move' && unit.onGround && !unit.ragdoll && !unit.floatie && !unit.pull && t.stamina >= JUMP_COST) {
          t.stamina -= JUMP_COST;
          unit.onGround = false;
          unit.fallStart = null;
          unit.vy = -JUMP_VY * this.level.jump;
          unit.vx = unit.facing * JUMP_VX * this.level.jump;
          unit.boat = null;
        }
        break;
      case 'aim':
        if (t.phase === 'aim') {
          t.aimAngle = clamp(input.angle, -Math.PI / 2, Math.PI / 2);
          t.aimPower = clamp(input.power, 0, 1);
          unit.aim = t.aimAngle;
        }
        break;
      case 'fire':
        if (t.phase === 'aim' && t.action && ACTIONS[t.action].kind === 'throw') {
          this.throwAt(unit, team, t.action as WeaponId, clamp(input.angle, -Math.PI / 2, Math.PI / 2), clamp(input.power, 0.1, 1));
        }
        break;
      case 'shoot':
        if (t.phase === 'aim' && t.action && ACTIONS[t.action].kind === 'hitscan') {
          this.shoot(unit, team, clamp(input.angle, -Math.PI / 2, Math.PI / 2), t.action as WeaponId);
        }
        break;
      case 'kick':
        if (t.phase === 'select' || t.phase === 'aim') this.melee(unit, team, 'boot');
        break;
      case 'skip':
        if (t.phase === 'select' || t.phase === 'move' || t.phase === 'aim') this.endTurn(unit);
        break;
    }
  }

  /** A player's whole team gives up the match: their moles leave the field and it resolves. */
  forfeit(playerId: string): void {
    const team = this.teams.find((t) => t.playerIds.includes(playerId));
    if (!team || this.ended) return;
    this.forfeitedTeams.add(team.team);
    for (const u of this.units) {
      if (!u.alive || u.team !== team.team) continue;
      u.alive = false;
      u.hp = 0;
      this.visual.push({ kind: 'death', x: u.x, y: u.y, team: u.team, cause: 'damage' });
    }
    this.visual.push({ kind: 'callout', text: `${team.name} forfeit` });
    this.turn.phase = 'resolve';
    this.settleTicks = 0;
  }

  private logTurnPlayed(playerId: string): void {
    if (playerId !== 'ai') this.events.push({ type: 'turnPlayed', actor: playerId });
  }

  private endTurn(unit: Unit, used: WeaponId | null = null): void {
    if (this.settings.mode === 'brawl') {
      // No turn to end: the thing just used is on cooldown and the mole plays on.
      unit.cooldown = cooldownFor(ACTIONS[used ?? unit.action ?? 'ball']);
      return;
    }
    this.logTurnPlayed(unit.playerId);
    unit.moveDir = 0;
    this.turn.phase = 'resolve';
    this.turn.action = null;
    this.settleTicks = 0;
  }

  /** The mole a player steers in a brawl: their one living mole. */
  liveUnit(playerId: string): Unit | null {
    return this.units.find((u) => u.alive && u.playerId === playerId) ?? null;
  }

  /**
   * Brawl input: every player drives their own mole at once. Walking and jumping are free;
   * an attack or a piece of gear goes the moment it is fired, then that mole waits out its
   * cooldown. Nothing here touches the shared turn state.
   */
  private applyLiveInput(playerId: string, input: PlayerInput): void {
    const unit = this.liveUnit(playerId);
    if (!unit) return;
    const team = this.teamOf(unit.team);
    if (!team) return;
    switch (input.kind) {
      case 'move':
        if (!unit.ragdoll && !unit.pull && !unit.floatie) {
          unit.moveDir = input.dir;
          if (input.dir !== 0) unit.facing = input.dir;
        } else if (input.dir !== 0) unit.facing = input.dir;
        break;
      case 'jump':
        if (unit.onGround && !unit.ragdoll && !unit.floatie && !unit.pull && unit.jumpCooldown <= 0) {
          unit.jumpCooldown = BRAWL_JUMP_TICKS;
          unit.onGround = false;
          unit.fallStart = null;
          unit.vy = -JUMP_VY * this.level.jump;
          unit.vx = unit.facing * JUMP_VX * this.level.jump;
          unit.boat = null;
        }
        break;
      case 'action': {
        const a = input.action;
        if (!isWeapon(a)) break;
        if ((team.ammo[a] ?? 0) <= 0) break;
        const def = ACTIONS[a];
        if (def.pveOnly) break;
        unit.action = a;
        // Instant things go at once: a swing, a medkit, a wall.
        if (unit.cooldown <= 0 && def.kind === 'melee') this.melee(unit, team, a);
        else if (unit.cooldown <= 0 && def.kind === 'use') {
          this.useGear(unit, team, a);
          unit.cooldown = cooldownFor(def);
        }
        // The last of a bought thing: the hand goes back to the ball.
        if ((team.ammo[a] ?? 0) <= 0) unit.action = 'ball';
        break;
      }
      case 'aim':
        unit.aim = clamp(input.angle, -Math.PI / 2, Math.PI / 2);
        break;
      case 'fire': {
        const a = unit.action;
        if (!a || !isWeapon(a) || unit.cooldown > 0 || unit.ragdoll || unit.pull) break;
        const def = ACTIONS[a];
        if (def.kind !== 'throw' || (team.ammo[a] ?? 0) <= 0) break;
        if (def.station && !this.catapultFor(unit)) break;
        this.throwAt(unit, team, a, clamp(input.angle, -Math.PI / 2, Math.PI / 2), clamp(input.power, 0.1, 1));
        if ((team.ammo[a] ?? 0) <= 0) unit.action = 'ball';
        break;
      }
      case 'shoot': {
        const a = unit.action;
        if (!a || !isWeapon(a) || unit.cooldown > 0 || unit.ragdoll || unit.pull) break;
        const def = ACTIONS[a];
        if (def.kind !== 'hitscan' || (team.ammo[a] ?? 0) <= 0) break;
        this.shoot(unit, team, clamp(input.angle, -Math.PI / 2, Math.PI / 2), a);
        if ((team.ammo[a] ?? 0) <= 0) unit.action = 'gun';
        break;
      }
      case 'kick':
        if (unit.cooldown <= 0) this.melee(unit, team, 'boot');
        break;
      default:
        break;
    }
  }

  private newShot(unit: Unit): string {
    this.shotHitSomething = false;
    return `${unit.playerId}:${++this.shotCounter}`;
  }

  private consume(team: SimTeam, weapon: WeaponId): void {
    if (team.ammo[weapon] !== Infinity) team.ammo[weapon]--;
    team.used[weapon] = (team.used[weapon] ?? 0) + 1;
  }

  // ---------- buy phase ----------

  private applyBuyInput(playerId: string, input: Extract<PlayerInput, { kind: 'buy' | 'ready' | 'recruit' | 'buyItem' }>, fromBot: boolean): void {
    const team = this.teamOfPlayer(playerId);
    if (!team || !this.liveTeams().includes(team)) return;
    if (team.isEnemyAi && !fromBot) return;
    if (input.kind === 'recruit') {
      if (this.recruit(team, input.unit, playerId)) {
        this.readyPlayers.delete(playerId);
        team.ready = false;
      }
      return;
    }
    if (input.kind === 'buyItem') {
      const item = ITEMS[input.item];
      if (!item || team.items[input.item] || team.coins < item.price) return;
      team.coins -= item.price;
      team.items[input.item] = true;
      this.readyPlayers.delete(playerId);
      team.ready = false;
      return;
    }
    if (input.kind === 'buy') {
      if (!isPurchasable(input.weapon) || !Number.isInteger(input.count) || input.count === 0) return;
      const count = clamp(input.count, -MAX_BUY_COUNT, MAX_BUY_COUNT);
      const cost = PRICES[input.weapon] * count;
      if (count > 0 && team.coins < cost) return;
      if (count < 0 && (team.ammo[input.weapon] ?? 0) < -count) return;
      team.coins -= cost;
      team.ammo[input.weapon] = (team.ammo[input.weapon] ?? 0) + count;
      // Changing the loadout un-readies the buyer so teammates see the final cart.
      this.readyPlayers.delete(playerId);
      team.ready = false;
      return;
    }
    this.readyPlayers.add(playerId);
    team.ready = team.isEnemyAi || team.playerIds.every((id) => this.readyPlayers.has(id) || this.autoplay.has(id));
  }

  private allReady(): boolean {
    return this.liveTeams().every((team) => team.ready);
  }

  /** Room left on a team for bought moles. */
  recruitSlots(team: SimTeam): number {
    return MAX_TEAM_SIZE - this.units.filter((u) => u.alive && u.team === team.team).length;
  }

  /** Why a recruit of this kind is not possible right now, or null if it is. */
  recruitBlocker(team: SimTeam, kind: RecruitKind): string | null {
    if (this.settings.mode === 'brawl') return 'One mole each in a brawl';
    if (this.recruitSlots(team) <= 0) return 'Crew is full';
    if (team.coins < RECRUIT_PRICES[kind]) return 'Not enough coins';
    const alive = this.units.filter((u) => u.alive);
    if (kind === 'floatie' && !freeFloat(this.level, team.side, alive)) return 'No floatie left';
    return null;
  }

  private recruit(team: SimTeam, kind: RecruitKind, ownerId: string): boolean {
    if (this.recruitBlocker(team, kind)) return false;
    const alive = this.units.filter((u) => u.alive);
    const owner = this.players.find((p) => p.id === ownerId);
    const ownerName = owner?.name ?? team.name;
    const count = this.units.filter((u) => u.team === team.team && u.ally).length + 1;
    let x: number;
    let y: number;
    if (kind === 'floatie') {
      const spot = freeFloat(this.level, team.side, alive)!;
      x = spot[0];
      y = spot[1];
    } else {
      const spot = freeSpawn(this.level, team.side, alive);
      x = spot[0];
      y = this.findGround(spot[0], spot[1]);
    }
    const facing: 1 | -1 = x < this.level.width / 2 ? 1 : -1;
    const name = kind === 'floatie' ? `${ownerName}'s floatie ${count}` : `${ownerName}'s mate ${count}`;
    const u = this.makeUnit(`${ownerId}#r${this.tick}-${count}`, ownerId, team.team, name, x, y, ALLY_HP[kind], team.isEnemyAi, facing);
    u.ally = true;
    if (kind === 'floatie') {
      u.floatie = true;
      u.onGround = true;
    }
    this.units.push(u);
    team.coins -= RECRUIT_PRICES[kind];
    this.visual.push({ kind: 'say', unitId: u.id, text: kind === 'floatie' ? 'Reporting!' : 'Aye!' });
    return true;
  }

  private startBuyPhase(teamIndex: number): void {
    const t = this.turn;
    if (isPvpMode(this.settings.mode)) {
      // Coin flip for who opens the fight (in a brawl, who the camera lands on first).
      const live = this.liveTeams();
      const pick = live[Math.floor(this.rng() * live.length)] ?? this.teams[teamIndex];
      teamIndex = Math.max(0, this.teams.indexOf(pick));
      this.visual.push({ kind: 'callout', text: `Coin flip: ${pick.name} goes first` });
    }
    this.fightFirstTeamIndex = teamIndex;
    this.pendingTeamIndex = teamIndex;
    this.readyPlayers.clear();
    const first = t.fight === 1 && t.wave === 1 && t.round <= 1;
    const income = coinsFor(first ? this.startingCoins : ROUND_INCOME, this.wealth);
    for (const team of this.liveTeams()) {
      team.coins += income;
      team.ready = false;
    }
    for (const team of this.teams) if (!this.liveTeams().includes(team)) team.ready = true;
    t.phase = 'buy';
    t.ticksLeft = BUY_TICKS;
    t.unitId = null;
    t.playerId = null;
    t.action = null;
    const label = this.settings.mode === 'coop' && t.wave > 1 ? `Wave ${t.wave} — buy phase`
      : t.bestOf > 1 ? `Round ${t.fight} of ${t.bestOf} — buy phase` : 'Buy phase';
    this.visual.push({ kind: 'callout', text: label });
  }

  /** Everyone back to their spawn with full HP for the next fight; bought allies are gone. */
  private resetField(): void {
    this.units = this.units.filter((u) => !u.ally);
    for (const u of this.units) {
      u.alive = true;
      u.hp = u.maxHp;
      u.x = u.spawnX;
      u.y = u.spawnY;
      u.vx = 0;
      u.vy = 0;
      u.onGround = false;
      u.ragdoll = false;
      u.spin = 0;
      u.downTicks = 0;
      u.floatie = false;
      u.fallStart = null;
      u.moveDir = 0;
      u.pose = 'idle';
      u.poseTicks = 0;
      u.hitFlash = 0;
      u.lastHitShot = null;
      u.lastHitBy = null;
      u.lastHitTick = -9999;
      u.facing = u.x < this.level.width / 2 ? 1 : -1;
    }
    for (const u of this.units) {
      u.boat = null;
      u.pull = null;
      u.flung = false;
      u.action = null;
      u.cooldown = 0;
      u.jumpCooldown = 0;
    }
    // Co-op rounds start the waves over: the raiders of the last round go, wave one comes back.
    if (this.settings.mode === 'coop') {
      this.units = this.units.filter((u) => u.playerId !== 'ai');
      this.wavesLeft = Math.max(1, this.settings.waves);
      this.turn.wave = 1;
      this.spawnEnemies();
    }
    this.projectiles = [];
    this.mines = [];
    this.catapults = this.makeCatapults();
    this.lastUnitByTeam.clear();
    for (const team of this.teams) team.revived = false;
    this.fightStartRound = this.turn.round;
    // Fresh ground and whole boats for the next fight.
    this.terrain.setMask(buildTerrain(this.level).solid);
    this.boats = buildBoats(this.level.boats);
    this.terrainEpoch++;
    this.visual.push({ kind: 'reset' });
  }

  private roundsNeeded(): number {
    return Math.ceil(this.turn.bestOf / 2);
  }

  /** One team is left standing (or the fight stalled). Score it; reset or finish the match. */
  private endFight(aliveTeams: SimTeam[]): void {
    const t = this.turn;
    const winner = aliveTeams.length === 1 ? aliveTeams[0] : null;
    if (winner) {
      t.wins[winner.team] = (t.wins[winner.team] ?? 0) + 1;
      for (const team of this.teams) team.coins += coinsFor(team === winner ? BOUNTIES.roundWin : BOUNTIES.roundLoss, this.wealth);
    }
    const best = Math.max(0, ...Object.values(t.wins));
    const leaders = this.teams.filter((team) => (t.wins[team.team] ?? 0) === best && best > 0);
    const decided = best >= this.roundsNeeded() || t.fight >= t.bestOf || this.forfeitedTeams.size > 0;
    if (decided) {
      this.ended = true;
      t.phase = 'ended';
      this.winnerTeam = leaders.length === 1 ? leaders[0].team : null;
      this.outcomes = this.players.map((p) => this.outcomeFor(p));
      this.visual.push({ kind: 'callout', text: this.winnerTeam === null ? 'Draw!' : `${this.teamOf(this.winnerTeam)?.name ?? 'Someone'} wins the match!` });
      return;
    }
    this.visual.push({ kind: 'callout', text: winner ? `${winner.name} takes round ${t.fight}` : `Round ${t.fight} is a draw` });
    t.fight++;
    t.phase = 'between';
    t.ticksLeft = 150;
    t.teamIndex = -1;
    t.unitId = null;
    t.playerId = null;
    this.resetPending = true;
    this.buyNext = true;
  }

  private throwAt(unit: Unit, team: SimTeam, weapon: WeaponId, angle: number, power: number): void {
    const def = ACTIONS[weapon];
    if (def.dig) {
      this.dig(unit, team, weapon, angle);
      return;
    }
    if (def.place === 'charge') {
      this.placeCharge(unit, team, weapon, angle, power);
      return;
    }
    if (def.place === 'self') {
      this.teleport(unit, team, weapon, angle, power);
      return;
    }
    if (def.place) {
      this.placePlatform(unit, team, weapon, angle, power);
      return;
    }
    if (def.strike) {
      this.airstrike(unit, team, weapon, angle, power);
      return;
    }
    if (def.station) {
      const station = this.catapultFor(unit);
      if (station) this.fling(unit, team, weapon, station, angle, power);
      return;
    }
    const mult = team.perks.throwMultiplier ?? 1;
    unit.aim = angle;
    unit.pose = 'throw';
    unit.poseTicks = 30;
    unit.held = null;
    const dirX = Math.cos(angle) * unit.facing;
    const dirY = -Math.sin(angle);
    const speed = launchSpeed(def, power, mult);
    const hx = unit.x + unit.facing * 10 + dirX * 24;
    const hy = unit.y - UNIT_H * 0.78 + dirY * 24;
    const homing = weapon === 'rpg' && team.homingLeft > 0;
    if (homing) team.homingLeft--;
    this.projectiles.push({
      id: ++this.projectileCounter, weapon, unitId: unit.id, x: hx, y: hy, vx: dirX * speed, vy: dirY * speed, owner: unit.playerId,
      team: unit.team, shotId: this.newShot(unit), originX: unit.x, fuse: def.fuseTicks ?? -1, bounces: 0,
      trail: def.gear ? 'trail.none' : this.trailFor(unit.playerId), alive: true, age: 0, homing, power,
    });
    if (!def.gear) this.shotPaths.set(this.projectileCounter, [[hx, hy]]);
    this.consume(team, weapon);
    if (def.gear) this.startGearMove(unit, weapon);
    else this.endTurn(unit, weapon);
  }

  /**
   * The mole climbs into the catapult's cup and is flung along the aim: it flies like a thrown
   * thing (the def's speed and the moles' own gravity), tumbling, and lands wherever it comes
   * down without fall damage. One launch comes off the station's pile; the move is spent, so
   * the mole still gets its attack from where it lands. The way across a chasm.
   */
  private fling(unit: Unit, team: SimTeam, weapon: WeaponId, station: Catapult, angle: number, power: number): void {
    const def = ACTIONS[weapon];
    const speed = launchSpeed(def, power, 1);
    const dirX = Math.cos(angle) * unit.facing;
    const dirY = -Math.sin(angle);
    station.dir = unit.facing;
    station.boulders--;
    unit.x = station.x + station.dir * 6;
    unit.y = station.y - CATAPULT_CUP_H;
    unit.vx = dirX * speed;
    unit.vy = dirY * speed;
    unit.onGround = false;
    unit.boat = null;
    unit.pull = null;
    unit.fallStart = null;
    unit.floatie = false;
    unit.ragdoll = true;
    unit.flung = true;
    unit.spin = 0;
    unit.downTicks = 0;
    unit.pose = 'ragdoll';
    unit.poseTicks = 0;
    unit.held = null;
    unit.aim = angle;
    unit.moveDir = 0;
    team.used[weapon] = (team.used[weapon] ?? 0) + 1;
    this.visual.push({ kind: 'catapult', id: station.id });
    this.visual.push({ kind: 'shake', power: 3 });
    this.say(unit, ['Wheee!', 'Incoming!', 'Hold my ball.', 'Geronimooo']);
    this.startGearMove(unit);
  }

  /** Set the plank (or the long bridge) down where the aim points. Refused (nothing spent, still aiming) if it would cut through a mole. */
  private placePlatform(unit: Unit, team: SimTeam, weapon: WeaponId, angle: number, power: number): void {
    const w = ACTIONS[weapon].span ?? PLATFORM_W;
    const spot = platformSpot(this.level, this.units, unit, angle, power, w);
    if (!spot.ok) {
      this.say(unit, ['Not there.', 'Someone is in the way.']);
      return;
    }
    unit.aim = angle;
    unit.pose = 'throw';
    unit.poseTicks = 20;
    unit.held = null;
    this.build(spot.x, spot.y, w, PLATFORM_H, 'plank');
    this.consume(team, weapon);
    this.startGearMove(unit);
  }

  /** Set a charge down where the aim points (within reach) and light it: it drops to whatever is below and blows on its fuse. */
  private placeCharge(unit: Unit, team: SimTeam, weapon: WeaponId, angle: number, power: number): void {
    const def = ACTIONS[weapon];
    const spot = platformSpot(this.level, this.units, unit, angle, power, 24);
    if (!spot.ok) {
      this.say(unit, ['Not on them.', 'Not there.']);
      return;
    }
    unit.aim = angle;
    unit.pose = 'throw';
    unit.poseTicks = 24;
    unit.held = weapon;
    this.projectiles.push({
      id: ++this.projectileCounter, weapon, unitId: unit.id, x: spot.x, y: spot.y, vx: 0, vy: 0, owner: unit.playerId,
      team: unit.team, shotId: this.newShot(unit), originX: unit.x, fuse: def.fuseTicks ?? 110, bounces: 0,
      trail: 'trail.none', alive: true, age: 0, homing: false, power,
    });
    this.say(unit, ['Fire in the hole!', 'Stand back.', 'Tick tick...']);
    this.consume(team, weapon);
    this.endTurn(unit, weapon);
  }

  /** Blink to where the aim points. Refused (nothing spent, still aiming) into solid ground or off the map. */
  private teleport(unit: Unit, team: SimTeam, weapon: WeaponId, angle: number, power: number): void {
    const spot = teleportSpot(this.level, (x, y) => this.isSolid(x, y), unit, angle, power);
    if (!spot.ok) {
      this.say(unit, ['Not into that.', 'Not there.']);
      return;
    }
    this.visual.push({ kind: 'teleport', x0: unit.x, y0: unit.y, x1: spot.x, y1: spot.y });
    if (unit.floatie) unit.floatie = false;
    unit.x = spot.x;
    unit.y = spot.y;
    unit.vx = 0;
    unit.vy = 0;
    unit.onGround = false;
    unit.boat = null;
    unit.pull = null;
    unit.ragdoll = false;
    // A drop from here counts as a fall.
    unit.fallStart = spot.y;
    unit.aim = angle;
    unit.pose = 'idle';
    unit.poseTicks = 0;
    unit.held = null;
    this.say(unit, ['Blink.', 'Whoa.', 'Over here!']);
    this.consume(team, weapon);
    this.startGearMove(unit);
  }

  /** Call bombs down on the marked ground: they fall in a line from above the map, the way the mole faces. */
  private airstrike(unit: Unit, team: SimTeam, weapon: WeaponId, angle: number, power: number): void {
    const def = ACTIONS[weapon];
    const n = def.strike ?? 5;
    const x = strikeSpot(this.level, unit, angle, power);
    unit.aim = angle;
    unit.pose = 'throw';
    unit.poseTicks = 24;
    unit.held = null;
    const shotId = this.newShot(unit);
    for (let i = 0; i < n; i++) {
      this.projectiles.push({
        id: ++this.projectileCounter, weapon, unitId: unit.id, x: x + (i - (n - 1) / 2) * STRIKE_GAP * unit.facing, y: -70 - i * 30,
        vx: unit.facing * 0.6, vy: 5, owner: unit.playerId, team: unit.team, shotId, originX: unit.x, fuse: -1, bounces: 0,
        trail: 'trail.none', alive: true, age: 0, homing: false, power: 1,
      });
    }
    this.visual.push({ kind: 'strike', x, dir: unit.facing });
    this.say(unit, ['Bring it down!', 'On my mark.', 'Incoming!']);
    this.consume(team, weapon);
    this.endTurn(unit, weapon);
  }

  /** Swing the pickaxe: a tunnel of bites along the aim, from the mole's middle outward. */
  private dig(unit: Unit, team: SimTeam, weapon: WeaponId, angle: number): void {
    unit.aim = angle;
    unit.pose = 'throw';
    unit.poseTicks = 26;
    unit.held = weapon;
    const dirX = Math.cos(angle) * unit.facing;
    const dirY = -Math.sin(angle);
    const cx = unit.x;
    const cy = unit.y - UNIT_H * 0.5;
    for (let i = 1; i <= DIG_BITES; i++) {
      const x = cx + dirX * (i * DIG_STEP + 6);
      const y = cy + dirY * (i * DIG_STEP + 6);
      this.carve(x, y, DIG_RADIUS);
      this.visual.push({ kind: 'dig', x, y, r: DIG_RADIUS });
    }
    this.visual.push({ kind: 'shake', power: 3 });
    this.consume(team, weapon);
    this.startGearMove(unit);
  }

  /**
   * Gear that flies (a hook, a plank) spends the move: the turn sits in the move phase with no
   * stamina until the thing has landed and the mole is back on its feet, then the menu returns
   * with Move greyed out.
   */
  private startGearMove(unit: Unit, used: WeaponId | null = null): void {
    if (this.settings.mode === 'brawl') {
      unit.cooldown = cooldownFor(ACTIONS[used ?? unit.action ?? 'grapple']);
      return;
    }
    const t = this.turn;
    t.action = null;
    t.phase = 'move';
    t.stamina = 0;
    t.moved = true;
    unit.moveDir = 0;
  }

  /** Instant gear used where the mole stands. Refused (nothing spent) when it would do nothing. */
  private useGear(unit: Unit, team: SimTeam, gear: WeaponId): void {
    const t = this.turn;
    if (gear === 'sandbags') {
      const cx = unit.x + unit.facing * SANDBAG_GAP;
      this.build(cx, unit.y - SANDBAG_H / 2, SANDBAG_W, SANDBAG_H, 'bags');
      this.say(unit, ['Dig in!', 'Cover!', 'Heavy...']);
    } else if (gear === 'medkit') {
      if (unit.hp >= unit.maxHp) return;
      const amount = Math.min(MEDKIT_HEAL, unit.maxHp - unit.hp);
      unit.hp += amount;
      this.visual.push({ kind: 'heal', x: unit.x, y: unit.y - UNIT_H, amount });
      this.say(unit, ['Better.', 'Ahh.', 'Good as new.']);
    } else if (gear === 'repair') {
      const boat = this.repairTarget(unit);
      if (!boat) return;
      const rebuilt = repairBoat(this.boats, boat);
      for (const r of rebuilt) this.visual.push({ kind: 'build', x: panelCenterX(r.boat, r.index), y: r.boat.y + r.boat.sink });
      this.visual.push({ kind: 'build', x: unit.x, y: boat.y + boat.sink });
      this.say(unit, ['Shipshape!', 'Patched.', 'Hold together...']);
    } else return;
    this.consume(team, gear);
    t.moved = true;
  }

  /** The damaged boat this mole stands on, if any: what a repair kit mends (hull, panels and pontoons). */
  repairTarget(unit: Unit): Boat | null {
    const b = unit.boat !== null ? this.boats[unit.boat] : null;
    if (!b || b.sunk) return null;
    return needsRepair(this.boats, b) ? b : null;
  }

  /** New ground: solid now, painted on every client, and anyone whose feet were inside it stands on top. */
  private build(cx: number, cy: number, w: number, h: number, fill: BuildFill): void {
    const op = this.terrain.build(cx, cy, w, h, fill);
    this.carves.push(op);
    const top = cy - h / 2;
    for (const u of this.units) {
      if (!u.alive || Math.abs(u.x - cx) > w / 2 + UNIT_HALF_W * 0.5) continue;
      if (u.y >= top && u.y <= cy + h / 2 + 2) {
        u.y = top - 1;
        u.onGround = true;
        u.boat = null;
        u.fallStart = null;
      }
    }
    this.visual.push({ kind: 'build', x: cx, y: cy });
  }

  /** Send a mole flying along a line: a hooked mole toward its hook, a harpooned enemy toward the gun. */
  private pullTo(u: Unit, x: number, y: number, left: number): void {
    if (u.floatie) {
      u.floatie = false;
      this.visual.push({ kind: 'callout', text: 'Overboard!' });
    }
    u.pull = { x, y, left };
    u.onGround = false;
    u.boat = null;
    u.fallStart = null;
    u.vx = 0;
    u.vy = 0;
    u.moveDir = 0;
    u.ragdoll = false;
    u.spin = 0;
    u.downTicks = 0;
    u.pose = left < 1e5 ? 'ragdoll' : 'idle';
    u.poseTicks = 0;
  }

  /**
   * Guns. One ray per round: the pistol, sniper, shotgun and harpoon fire one; the SMG fires a
   * fan of them. A ray stops at the first mole or solid it meets, unless the gun pierces, in
   * which case it runs its whole length and hurts every mole along it.
   */
  private shoot(unit: Unit, team: SimTeam, angle: number, weapon: WeaponId = 'gun'): void {
    const def = ACTIONS[weapon];
    unit.aim = angle;
    unit.pose = 'gun';
    unit.poseTicks = 30;
    const shotId = this.newShot(unit);
    const n = def.pellets ?? 1;
    const spread = def.spread ?? 0;
    this.visual.push({ kind: 'shake', power: weapon === 'shotgun' ? 4 : weapon === 'railgun' ? 5 : 2 });
    let anyHit = false;
    let ghost: [number, number][] | null = null;
    for (let k = 0; k < n; k++) {
      const a = n > 1 ? angle + ((k / (n - 1)) - 0.5) * spread + (this.rng() - 0.5) * spread * 0.3 : angle;
      const dx = Math.cos(a) * unit.facing;
      const dy = -Math.sin(a);
      let px = unit.x + unit.facing * 14 + dx * 26;
      let py = unit.y - UNIT_H * 0.62 + dy * 26;
      const x0 = px;
      const y0 = py;
      const hits: Unit[] = [];
      const steps = def.range ? Math.ceil(def.range / 2) : 1400;
      for (let i = 0; i < steps; i++) {
        px += dx * 2;
        py += dy * 2;
        if (px < -50 || px > this.level.width + 50 || py > this.level.height + 50 || py < -400) break;
        const u = this.unitAt(px, py, unit);
        if (u && !hits.includes(u)) {
          hits.push(u);
          if (!def.pierce) break;
        }
        if (!def.pierce && this.isSolid(px, py)) break;
      }
      this.visual.push({ kind: 'shot', x0, y0, x1: px, y1: py, weapon });
      if (!ghost) ghost = [[x0, y0], [px, py]];
      if (hits.length === 0 && !def.pull && !def.pierce) {
        this.carve(px, py, weapon === 'shotgun' ? 7 : 4);
        this.chipBoats(px, py, def.damage * 0.8, unit, shotId);
      }
      for (const hit of hits) {
        anyHit = true;
        // Short-range guns fade to 40% at the end of their reach.
        const hx = hit.x;
        const hy = Math.max(hit.y - UNIT_H, Math.min(hit.y, py));
        const dist = Math.hypot(hx - x0, hy - y0);
        const fade = def.range ? clamp(1 - (dist / def.range) * 0.6, 0.4, 1) : 1;
        // Where this ray crosses the mole's column decides a headshot.
        const crossY = y0 + (Math.abs(dx) > 1e-6 ? ((hx - x0) / dx) * dy : hy - y0);
        const headshot = (def.headshot ?? 1) > 1 && crossY < hit.y - (UNIT_H - HEAD_ZONE);
        const dmg = Math.round(def.damage * fade * (headshot ? def.headshot ?? 1.5 : 1));
        if (def.pull) {
          this.pullTo(hit, unit.x + unit.facing * 24, unit.y - UNIT_H * 0.5, def.pull);
          this.say(hit, ['Hey, let go!', 'Reeled in!', 'Not like this—']);
        } else {
          const kb = def.knockback * fade;
          this.knock(hit, dx * kb, dy * kb - 1.5, kb > 5);
        }
        this.damage(hit, dmg, unit, shotId, weapon, headshot, true, Math.abs(hx - unit.x) / this.level.width);
      }
      if (hits.length === 0 && !def.pull && !def.pierce) this.visual.push({ kind: 'explosion', x: px, y: py, r: 6 });
    }
    if (ghost) this.setGhost(team.team, { weapon, path: ghost });
    if (!anyHit && def.pierce) this.say(unit, WHIFF);
    this.consume(team, weapon);
    this.endTurn(unit, weapon);
  }

  /**
   * Close quarters. The boot kicks up and away; the bat swings flat and far; the mallet does
   * damage and cracks the raft panel under the victim instead of launching them; the pike
   * reaches twice as far and shoves.
   */
  private melee(unit: Unit, team: SimTeam, weapon: WeaponId): void {
    const def = ACTIONS[weapon];
    const target = this.meleeTarget(unit, def.reach ?? 64);
    if (!target) return;
    const mult = team.perks.bootMultiplier ?? 1;
    unit.facing = target.x >= unit.x ? 1 : -1;
    if (weapon === 'boot') {
      unit.pose = 'kick';
    } else {
      unit.pose = 'throw';
      unit.aim = weapon === 'pike' ? 0.1 : 0.35;
      unit.held = weapon;
    }
    unit.poseTicks = 26;
    const shotId = this.newShot(unit);
    this.visual.push({ kind: 'kick', x: target.x, y: target.y - UNIT_H / 2, dir: unit.facing, weapon });
    this.visual.push({ kind: 'shake', power: weapon === 'mallet' ? 6 : 4 });
    const kb = def.knockback * mult;
    switch (weapon) {
      case 'bat':
        this.knock(target, unit.facing * kb, -5 * mult, true);
        break;
      case 'mallet': {
        this.knock(target, unit.facing * kb, -2, false);
        const b = target.boat !== null ? this.boats[target.boat] : null;
        if (b && !b.sunk) {
          const broke = damageBoat(b, target.x, b.y + b.sink, 8, MALLET_PANEL_DAMAGE);
          for (const i of broke) this.visual.push({ kind: 'panel', x: panelCenterX(b, i), y: b.y + b.sink });
          if (b.sunk) this.visual.push({ kind: 'callout', text: 'Going down!' });
        }
        break;
      }
      case 'pike':
        this.knock(target, unit.facing * kb, -4, true);
        break;
      default:
        this.knock(target, unit.facing * kb, -8.5 * mult, true);
    }
    this.damage(target, def.damage, unit, shotId, weapon, false, true, 0);
    if (target.alive) this.say(target, KICKED);
    this.consume(team, weapon);
    this.endTurn(unit, weapon);
  }

  // ---------- tick ----------

  step(): void {
    if (this.ended) return;
    this.tick++;
    updateBoats(this.boats);
    this.updateUnits();
    this.updateProjectiles();
    this.updateMines();
    this.updateTurn();
    this.updateLastStand();
    this.checkEnd();
  }

  private updateTurn(): void {
    const t = this.turn;
    switch (t.phase) {
      case 'select':
      case 'move':
      case 'aim': {
        t.ticksLeft--;
        const u = this.activeUnit();
        if (!u) {
          t.phase = 'resolve';
          this.settleTicks = 0;
          break;
        }
        // Out of stamina (or gear in the air): the move ends once everything has landed.
        if (t.phase === 'move' && t.stamina <= 0 && u.onGround && !u.pull && !this.projectiles.some((p) => p.alive)) {
          u.moveDir = 0;
          t.phase = 'select';
          t.moved = true;
        }
        if (t.ticksLeft <= 0) this.endTurn(u);
        break;
      }
      case 'live': {
        // Mines are live as soon as they settle; the round is a draw when the clock runs out.
        for (const m of this.mines) m.armed = true;
        t.ticksLeft--;
        if (t.ticksLeft <= 0) this.endFight([]);
        break;
      }
      case 'resolve': {
        const busy = this.projectiles.some((p) => p.alive) || this.mines.some((m) => m.fuse >= 0)
          || this.units.some((u) => u.alive && (!u.onGround || u.ragdoll || u.downTicks > 0 || Math.abs(u.vx) > 0.05));
        this.settleTicks = busy ? 0 : this.settleTicks + 1;
        if (this.settleTicks > 16) {
          t.phase = 'between';
          t.ticksLeft = BETWEEN_TICKS;
          this.afterTurn();
        }
        break;
      }
      case 'between':
        t.ticksLeft--;
        if (t.ticksLeft <= 0) {
          if (this.resetPending) {
            this.resetPending = false;
            this.resetField();
          }
          this.nextTurn();
        }
        break;
      case 'buy':
        t.ticksLeft--;
        if (this.allReady() || t.ticksLeft <= 0) this.beginTurn(this.pendingTeamIndex);
        break;
      case 'ended':
        break;
    }
  }

  private afterTurn(): void {
    // Mines planted this turn are live from here on.
    for (const m of this.mines) m.armed = true;
    if (this.settings.mode === 'coop') {
      const enemiesAlive = this.units.some((u) => u.alive && u.team === 1);
      if (!enemiesAlive && this.wavesLeft > 1) {
        this.wavesLeft--;
        this.turn.wave++;
        this.spawnEnemies();
        this.visual.push({ kind: 'wave', wave: this.turn.wave });
        // Restock before the next wave hits.
        this.buyNext = true;
      }
    }
  }

  private nextTurn(): void {
    const t = this.turn;
    const aliveTeams = this.teams.filter((team) => this.units.some((u) => u.alive && u.team === team.team));
    if (aliveTeams.length === 0) return;
    let idx = t.teamIndex;
    let found = false;
    for (let i = 0; i < this.teams.length; i++) {
      idx = (idx + 1) % this.teams.length;
      if (idx === this.fightFirstTeamIndex) t.round++;
      if (aliveTeams.includes(this.teams[idx])) {
        found = true;
        break;
      }
    }
    if (!found) return;
    // The shop opens before the first turn of a match, a fight or a co-op wave.
    if (this.buyNext) {
      this.buyNext = false;
      this.startBuyPhase(idx);
    } else {
      this.beginTurn(idx);
    }
  }

  private beginTurn(idx: number): void {
    const t = this.turn;
    if (this.settings.mode === 'brawl') {
      this.beginLive(idx);
      return;
    }
    const team = this.teams[idx];
    if (!team) return;
    const teamUnits = this.units.filter((u) => u.alive && u.team === team.team);
    if (teamUnits.length === 0) {
      t.teamIndex = idx;
      t.phase = 'between';
      t.ticksLeft = 1;
      return;
    }
    t.teamIndex = idx;
    const lastIdx = teamUnits.findIndex((u) => u.id === this.lastUnitByTeam.get(team.team));
    const unit = teamUnits[(lastIdx + 1) % teamUnits.length];
    this.lastUnitByTeam.set(team.team, unit.id);
    this.rollWind();
    t.phase = 'select';
    t.unitId = unit.id;
    t.playerId = unit.playerId;
    t.ticksLeft = t.turnTicks;
    t.action = null;
    t.stamina = STAMINA * this.level.stamina;
    t.moved = false;
    t.aimAngle = unit.aim;
    t.aimPower = 0.6;
    unit.moveDir = 0;
    this.visual.push({ kind: 'turn', playerId: unit.playerId, unitId: unit.id });
  }

  /** Brawl: the round goes live for everyone at once, with a short grace before anything may fire. */
  private beginLive(idx: number): void {
    const t = this.turn;
    t.teamIndex = idx;
    t.phase = 'live';
    t.unitId = null;
    t.playerId = null;
    t.ticksLeft = BRAWL_SECONDS * 60;
    t.action = null;
    t.stamina = STAMINA * this.level.stamina;
    t.moved = false;
    this.rollWind();
    for (const u of this.units) {
      if (!u.alive) continue;
      u.action = 'ball';
      u.cooldown = 90;
      u.jumpCooldown = 0;
      u.moveDir = 0;
    }
    this.visual.push({ kind: 'callout', text: 'Fight!' });
  }

  // ---------- units ----------

  private bodyBlocked(x: number, y: number): boolean {
    for (let dy = 6; dy < UNIT_H; dy += 8) {
      if (this.isSolid(x - UNIT_HALF_W * 0.6, y - dy) || this.isSolid(x + UNIT_HALF_W * 0.6, y - dy)) return true;
    }
    return false;
  }

  private footSolid(x: number, y: number): boolean {
    return this.isSolid(x, y) || this.isSolid(x - UNIT_HALF_W * 0.55, y) || this.isSolid(x + UNIT_HALF_W * 0.55, y);
  }

  private updateUnits(): void {
    const t = this.turn;
    for (const u of this.units) {
      if (!u.alive) continue;
      if (u.hitFlash > 0) u.hitFlash--;
      if (u.poseTicks > 0) {
        u.poseTicks--;
        if (u.poseTicks === 0) {
          u.held = null;
          if (!u.ragdoll) u.pose = 'idle';
        }
      }
      if (u.downTicks > 0) {
        u.downTicks--;
        if (u.downTicks === 0) u.pose = 'idle';
      }

      // On a line: no gravity, no walking; straight toward the point until it arrives, is blocked,
      // or (a harpooned mole) has been dragged far enough. Then it drops like anything else.
      if (u.pull) {
        const cx = u.x;
        const cy = u.y - UNIT_H * 0.5;
        const dx = u.pull.x - cx;
        const dy = u.pull.y - cy;
        const d = Math.hypot(dx, dy);
        const step = Math.min(PULL_SPEED, d, u.pull.left);
        const nx = clamp(cx + (dx / (d || 1)) * step, UNIT_HALF_W, this.level.width - UNIT_HALF_W);
        const ny = u.y + (dy / (d || 1)) * step;
        const arrived = d <= PULL_SPEED + 2 || u.pull.left <= step;
        const blocked = this.bodyBlocked(nx, ny) || (dy > 0 && this.footSolid(nx, ny + 1));
        if (!blocked) {
          u.x = nx;
          u.y = ny;
          u.pull.left -= step;
        }
        if (arrived || blocked) {
          const dragged = u.pull.left < 1e5;
          u.vx = (dx / (d || 1)) * (dragged ? 3 : 2);
          u.vy = (dy / (d || 1)) * 2;
          u.pull = null;
          u.fallStart = null;
          if (dragged) {
            u.ragdoll = true;
            u.pose = 'ragdoll';
          }
          if (this.footSolid(u.x, u.y + 1)) this.land(u);
        }
        if (u.y > this.deathY + 10) this.kill(u, this.level.waterY !== null ? 'drown' : 'fall');
        continue;
      }

      const live = t.phase === 'live';
      if (live) {
        if (u.cooldown > 0) u.cooldown--;
        if (u.jumpCooldown > 0) u.jumpCooldown--;
      }
      const active = live || (u.id === t.unitId && t.phase === 'move');
      // Walking (only in the move phase, only while stamina lasts)
      if (active && u.moveDir !== 0 && u.onGround && !u.ragdoll) {
        const nx = u.x + u.moveDir * WALK_SPEED;
        const deck = u.boat !== null ? this.boats[u.boat] : null;
        if (deck && crossesStop(deck, u.x, nx)) {
          u.moveDir = 0; // the stop post at the deck's end
        } else if (nx > UNIT_HALF_W && nx < this.level.width - UNIT_HALF_W) {
          let placed = false;
          for (let up = 0; up <= STEP_UP; up += 2) {
            const ny = u.y - up;
            if (this.footSolid(nx, ny)) continue;
            if (this.bodyBlocked(nx, ny)) break;
            u.x = nx;
            u.y = ny;
            placed = true;
            break;
          }
          if (placed) {
            if (!live) t.stamina -= WALK_SPEED;
            u.walkPhase += 0.28;
            u.pose = 'walk';
            // Stepping from a deck to a ramp, or off the end onto the shore, changes what it stands on.
            if (!u.floatie) u.boat = this.boatIndexAt(u.x, u.y + 1);
          } else {
            u.moveDir = 0;
          }
        } else {
          u.moveDir = 0;
        }
      } else if (u.pose === 'walk') {
        u.pose = 'idle';
      }

      // Leaving the ground
      // Riding a boat: follow the deck as it lists and sinks, slide when it is steep, fall through holes.
      if (u.onGround && u.boat !== null && !u.floatie) {
        const b = this.boats[u.boat];
        const d = b ? deckY(b, u.x) : null;
        if (!b || d === null) {
          u.onGround = false;
          u.boat = null;
          u.fallStart = null;
        } else {
          u.y = d;
          if (Math.abs(b.angle) > SLIDE_ANGLE && !u.ragdoll) {
            u.vx += Math.tan(b.angle) * 0.45;
            const nx = clamp(u.x + u.vx, UNIT_HALF_W, this.level.width - UNIT_HALF_W);
            if (crossesStop(b, u.x, nx) || this.bodyBlocked(nx, u.y)) u.vx = 0;
            else {
              u.x = nx;
              const d2 = deckY(b, u.x);
              if (d2 !== null) u.y = d2;
            }
          }
        }
      }

      if (u.onGround && !u.floatie && !this.footSolid(u.x, u.y + 1)) {
        u.onGround = false;
        u.boat = null;
        if (!u.ragdoll) u.fallStart = u.y;
      }

      if (!u.onGround) {
        u.vy = Math.min(u.vy + GRAVITY * this.level.gravity, MAX_FALL);
        if (u.ragdoll) u.spin += u.vx * 0.025 + 0.02 * Math.sign(u.vx || 1);
        // Horizontal: a jumping mole steers a little, and sliding up a wall is not a rebound.
        if (active && u.moveDir !== 0 && !u.ragdoll) u.vx += (u.moveDir * JUMP_VX * this.level.jump - u.vx) * AIR_STEER;
        const nx = u.x + u.vx;
        if (nx > UNIT_HALF_W && nx < this.level.width - UNIT_HALF_W && !this.bodyBlocked(nx, u.y)) u.x = nx;
        else if (u.ragdoll || !active) u.vx *= -0.45;
        else u.vx *= 0.5;
        // Vertical in small steps so nothing tunnels through a raft
        let remaining = u.vy;
        const sign = Math.sign(remaining);
        while (Math.abs(remaining) > 0) {
          const dy = sign * Math.min(3, Math.abs(remaining));
          remaining -= dy;
          const ny = u.y + dy;
          if (dy > 0 && this.footSolid(u.x, ny)) {
            this.land(u);
            break;
          }
          if (dy < 0 && this.isSolid(u.x, ny - UNIT_H)) {
            u.vy = 0;
            break;
          }
          u.y = ny;
        }
        u.vx *= 0.995;
      } else {
        // Less grip on a tilting deck so a list actually moves people.
        u.vx *= u.boat !== null ? 0.92 : 0.6;
        if (Math.abs(u.vx) < 0.05) u.vx = 0;
        if (u.ragdoll && u.downTicks === 0 && Math.abs(u.vx) < 0.1) {
          u.ragdoll = false;
          u.spin = 0;
        }
      }

      if (u.y > this.deathY + 10) this.kill(u, this.level.waterY !== null ? 'drown' : 'fall');
    }
  }

  private land(u: Unit): void {
    let y = u.y;
    for (let i = 0; i < 14 && this.footSolid(u.x, y); i++) y -= 1;
    u.y = y;
    const impact = u.vy;
    u.onGround = true;
    // Whatever it was standing on before, it is on solid ground now (maybe a deck).
    u.floatie = false;
    u.boat = this.boatIndexAt(u.x, u.y + 1);
    const team = this.teamOf(u.team);
    const mult = team?.perks.fallDamageMultiplier ?? 1;
    if (u.ragdoll) {
      // A catapult landing is part of the ride: no harm, however hard it comes down.
      if (impact > 9 && !u.flung) {
        const dmg = Math.round((impact - 9) * 4 * mult);
        if (dmg > 0) this.hurt(u, dmg, 'fall');
      }
      if (impact > 7) {
        // Comedic flop: bounce once, keep tumbling.
        u.vy = -impact * 0.25;
        u.vx *= 0.6;
        u.onGround = false;
        u.spin += 0.4;
        return;
      }
      u.vy = 0;
      u.vx *= 0.3;
      u.downTicks = u.flung ? 10 : 28;
      u.flung = false;
      u.pose = 'ragdoll';
      u.spin = 0;
      return;
    }
    const fell = u.fallStart !== null ? u.y - u.fallStart : 0;
    u.fallStart = null;
    if (fell > SOFT_FALL) {
      const dmg = Math.round(((fell - SOFT_FALL) / 5) * mult);
      if (dmg > 0) this.hurt(u, dmg, 'fall');
    }
    u.vy = 0;
    u.vx *= 0.3;
  }

  /** Environmental damage (falls). Credited to the last attacker if recent. */
  private hurt(u: Unit, dmg: number, cause: 'fall'): void {
    u.hp -= dmg;
    u.hitFlash = 8;
    this.visual.push({ kind: 'damage', x: u.x, y: u.y - UNIT_H, amount: dmg, team: u.team, headshot: false });
    if (u.hp <= 0) this.kill(u, cause);
  }

  private knock(u: Unit, vx: number, vy: number, hard: boolean): void {
    if (u.floatie) {
      // A tap only rocks the floatie; anything stronger tips the mole off it.
      if (Math.hypot(vx, vy) < FLOATIE_KNOCKOFF) {
        u.hitFlash = Math.max(u.hitFlash, 6);
        return;
      }
      u.floatie = false;
      this.visual.push({ kind: 'callout', text: 'Overboard!' });
    }
    u.vx += vx;
    u.vy += vy;
    u.onGround = false;
    u.fallStart = null;
    u.moveDir = 0;
    u.pull = null;
    // Hit on the way: the soft landing is off.
    u.flung = false;
    if (hard || Math.hypot(vx, vy) > 5) {
      u.ragdoll = true;
      u.pose = 'ragdoll';
      u.poseTicks = 0;
      u.downTicks = 0;
    }
  }

  private unitAt(x: number, y: number, except?: Unit): Unit | null {
    for (const u of this.units) {
      if (!u.alive || u === except) continue;
      if (Math.abs(x - u.x) <= UNIT_HALF_W && y <= u.y && y >= u.y - UNIT_H) return u;
    }
    return null;
  }

  // ---------- projectiles ----------

  /** A bomblet flies by its own little definition; everything else by its weapon's. */
  private defOf(p: Projectile): ActionDef {
    return p.frag ? BOMBLET : ACTIONS[p.weapon];
  }

  private updateProjectiles(): void {
    for (const p of this.projectiles) {
      if (!p.alive) continue;
      p.age++;
      const def = this.defOf(p);
      // Wind pushes the lobbed things; rockets, hooks, bullets and dropped charges ignore it.
      if (this.settings.wind && def.kind === 'throw' && def.gravity >= 0.25 && def.speed > 0 && !p.homing) p.vx += this.wind * 0.04;
      if (p.homing && p.age > 12) {
        const target = this.nearestEnemy(p.x, p.y, p.team);
        if (target) {
          const dx = target.x - p.x;
          const dy = target.y - UNIT_H / 2 - p.y;
          const d = Math.hypot(dx, dy) || 1;
          const speed = Math.max(12, Math.hypot(p.vx, p.vy));
          p.vx += (dx / d) * 1.2;
          p.vy += (dy / d) * 1.2;
          const ns = Math.hypot(p.vx, p.vy) || 1;
          p.vx = (p.vx / ns) * speed;
          p.vy = (p.vy / ns) * speed;
        }
      } else {
        p.vy += def.gravity * this.level.gravity;
      }
      // A sticky bomb that has bitten stays where it is, or rides the mole it hit, until its fuse runs out.
      if (p.stuck) {
        const host = p.stuckTo ? this.units.find((u) => u.id === p.stuckTo && u.alive) : undefined;
        if (host) {
          p.x = host.x;
          p.y = host.y - UNIT_H * 0.5;
        } else if (p.stuckTo) p.stuckTo = null;
        if (p.fuse > 0) {
          p.fuse--;
          if (p.fuse === 0) this.explode(p, p.x, p.y, host ?? null);
        } else p.alive = false;
        continue;
      }
      if (p.fuse > 0) {
        p.fuse--;
        if (p.fuse === 0) {
          if (def.deploy === 'mine') this.settleMine(p);
          else this.explode(p, p.x, p.y, null);
          continue;
        }
      }
      // A hook flies straight through moles; grenade-like things bounce off them; the rest goes off on contact.
      const passesUnits = def.deploy === 'hook';
      const bouncesOffUnits = p.weapon === 'grenade' || p.weapon === 'bignade' || p.weapon === 'mine' || p.weapon === 'rubber' || !!p.frag;
      const steps = Math.max(1, Math.ceil(Math.hypot(p.vx, p.vy) / 3));
      const sx = p.vx / steps;
      const sy = p.vy / steps;
      for (let i = 0; i < steps && p.alive; i++) {
        const nx = p.x + sx;
        const ny = p.y + sy;
        if (nx < -60 || nx > this.level.width + 60 || ny > this.level.height + 60) {
          p.alive = false;
          break;
        }
        if (this.level.waterY !== null && ny > this.level.waterY) {
          p.alive = false;
          this.visual.push({ kind: 'splash', x: nx, y: this.level.waterY });
          if (!this.shotHitSomething && !def.gear) this.whiff(p);
          break;
        }
        if (def.bounce !== undefined && this.stopBlocks(p.x, p.y, nx)) {
          // Rolled into a stop post: it holds the grenade on deck.
          p.vx = -p.vx * 0.4;
          break;
        }
        const shooter = p.age < 10 ? this.units.find((u) => u.id === p.unitId) : undefined;
        const hit = passesUnits ? null : this.unitAt(nx, ny, shooter);
        if (hit) {
          if (p.weapon === 'ball') {
            this.shotHitSomething = true;
            const dir = Math.hypot(p.vx, p.vy) || 1;
            // Shove scales with how fast the ball arrives: a lob nudges, a full-power throw launches.
            const kb = def.knockback * clamp(dir / 20, 0.45, 1.6) * (this.teamOf(p.team)?.perks.throwMultiplier ?? 1);
            this.knock(hit, (p.vx / dir) * kb, (p.vy / dir) * kb - 4, true);
            const headshot = ny < hit.y - (UNIT_H - HEAD_ZONE);
            this.damage(hit, Math.round(def.damage * (headshot ? 1.5 : 1)), this.ownerUnit(p), p.shotId, 'ball', headshot, true, Math.abs(nx - p.originX) / this.level.width, p.owner, p.team);
            this.visual.push({ kind: 'explosion', x: nx, y: ny, r: 10 });
            p.alive = false;
            break;
          }
          if (bouncesOffUnits) {
            p.vx = -(p.vx * (p.weapon === 'rubber' ? 0.8 : 0.35));
            p.vy = -Math.abs(p.vy) * (p.weapon === 'rubber' ? 0.8 : 0.35);
            p.bounces++;
            break;
          }
          if (def.stick) {
            this.stickTo(p, nx, ny, hit);
            break;
          }
          this.explode(p, nx, ny, hit);
          break;
        }
        if (this.isSolid(nx, ny)) {
          if (def.deploy === 'hook') {
            this.hookBite(p, nx, ny);
            break;
          }
          if (def.stick) {
            this.stickTo(p, p.x, p.y, null);
            break;
          }
          if (def.split && !p.frag) {
            // A cluster bomb pops the moment it touches down.
            this.explode(p, p.x, p.y, null);
            break;
          }
          if (def.bounce !== undefined) {
            this.bounce(p, nx, ny, def.bounce);
            if (def.maxBounces !== undefined && p.bounces > def.maxBounces) {
              p.alive = false;
              if (!this.shotHitSomething) this.whiff(p);
            }
            break;
          }
          this.explode(p, nx, ny, null);
          break;
        }
        p.x = nx;
        p.y = ny;
      }
      if (p.alive && p.weapon === 'ball' && p.age > 360) p.alive = false;
      // A hook that bites nothing within its reach is reeled back in.
      if (p.alive && def.deploy === 'hook' && p.age > 34) p.alive = false;
      // A mine that has come to rest is a mine.
      if (p.alive && def.deploy === 'mine' && p.bounces > 0 && Math.hypot(p.vx, p.vy) < 0.4 && this.isSolid(p.x, p.y + 2)) this.settleMine(p);
      const path = this.shotPaths.get(p.id);
      if (path && p.alive && p.age % 2 === 0) path.push([Math.round(p.x), Math.round(p.y)]);
    }
    if (this.projectiles.some((p) => !p.alive)) {
      for (const p of this.projectiles) if (!p.alive) this.finishShot(p);
      this.projectiles = this.projectiles.filter((p) => p.alive);
    }
  }

  /** A thrown shot ended (blast, splash, off the map): keep its path as the team's last shot. */
  private finishShot(p: Projectile): void {
    const path = this.shotPaths.get(p.id);
    if (!path) return; // cluster fragments and restored projectiles have no path
    this.shotPaths.delete(p.id);
    path.push([Math.round(p.x), Math.round(p.y)]);
    this.setGhost(p.team, { weapon: p.weapon, path });
  }

  private setGhost(teamId: number, ghost: ShotGhost): void {
    const team = this.teamOf(teamId);
    if (!team) return;
    team.lastShot = ghost;
    this.ghosts = this.ghosts.filter((g) => g.team !== teamId);
    this.ghosts.push({ team: teamId, ghost });
  }

  private ownerUnit(p: Projectile): Unit | null {
    return this.units.find((u) => u.playerId === p.owner && u.team === p.team) ?? null;
  }

  private whiff(p: Projectile): void {
    const u = this.ownerUnit(p);
    if (u && this.rng() < 0.5) this.say(u, WHIFF);
  }

  /**
   * Velocity after glancing off the solid at (x, y): the surface normal is estimated from eight
   * samples around the contact point, like the bots' trajectory preview, which calls this too.
   * A ball that comes down almost flat loses a little speed to rolling friction so it settles
   * on a deck instead of trundling off the end.
   */
  reflect(x: number, y: number, vx: number, vy: number, restitution: number): { vx: number; vy: number } {
    let nxs = 0;
    let nys = 0;
    for (let a = 0; a < 8; a++) {
      const ang = (a / 8) * Math.PI * 2;
      if (this.isSolid(x + Math.cos(ang) * 4, y + Math.sin(ang) * 4)) {
        nxs -= Math.cos(ang);
        nys -= Math.sin(ang);
      }
    }
    const len = Math.hypot(nxs, nys);
    if (len < 0.01) {
      nxs = 0;
      nys = -1;
    } else {
      nxs /= len;
      nys /= len;
    }
    const dot = vx * nxs + vy * nys;
    let rvx = (vx - 2 * dot * nxs) * restitution;
    let rvy = (vy - 2 * dot * nys) * restitution;
    if (Math.abs(rvy) < 1.5) rvx *= 0.85;
    if (Math.abs(rvx) < 0.2 && Math.abs(rvy) < 0.2) {
      rvx = 0;
      rvy = 0;
    }
    return { vx: rvx, vy: rvy };
  }

  /** A low projectile rolling from x to nx along a deck meets a stop post. */
  stopBlocks(x: number, y: number, nx: number): boolean {
    for (const b of this.boats) {
      if (b.sunk) continue;
      const d = deckY(b, x) ?? deckY(b, nx);
      if (d === null || y < d - STOP_H || y > d + 2) continue;
      if (crossesStop(b, x, nx)) return true;
    }
    return false;
  }

  private bounce(p: Projectile, nx: number, ny: number, restitution: number): void {
    const r = this.reflect(nx, ny, p.vx, p.vy, restitution);
    p.vx = r.vx;
    p.vy = r.vy;
    p.bounces++;
    const team = this.teamOf(p.team);
    if (p.weapon === 'grenade' && p.bounces === 1 && team?.perks.clusterGrenade) {
      for (let i = -1; i <= 1; i += 2) {
        this.projectiles.push({ ...p, id: ++this.projectileCounter, vx: p.vx + i * 2.4, vy: p.vy - 2.5, fuse: Math.max(40, p.fuse), bounces: 1 });
      }
    }
  }


  private nearestEnemy(x: number, y: number, team: number): Unit | null {
    let best: Unit | null = null;
    let bestD = Infinity;
    for (const u of this.units) {
      if (!u.alive || u.team === team) continue;
      const d = Math.hypot(u.x - x, u.y - y);
      if (d < bestD) {
        bestD = d;
        best = u;
      }
    }
    return best;
  }

  private explode(p: Projectile, x: number, y: number, direct: Unit | null): void {
    p.alive = false;
    const def = this.defOf(p);
    // A hard throw blows people farther than a lob dropped at your feet.
    const throwFactor = p.weapon === 'rpg' || p.weapon === 'mortar' || p.weapon === 'catapult' || p.weapon === 'airstrike' ? 1 : 0.6 + 0.6 * p.power;
    // A bolt that misses sticks in the ground.
    if (p.weapon === 'crossbow' && !direct) this.visual.push({ kind: 'bolt', x, y, angle: Math.atan2(p.vy, p.vx) });
    const anyEnemy = this.blast(x, y, def, p.weapon, p.owner, p.team, p.shotId, p.originX, throwFactor, direct);
    if (anyEnemy) this.shotHitSomething = true;
    else if (!this.shotHitSomething && !p.frag) this.whiff(p);
    if (def.split && !p.frag) this.splitInto(p, def.split);
  }

  /** An explosion at (x, y) by `def`: crater, broken panels, shoves and damage. Returns whether an enemy was caught. */
  private blast(
    x: number, y: number, def: ActionDef, weapon: WeaponId, owner: string, team: number, shotId: string, originX: number,
    throwFactor: number, direct: Unit | null,
  ): boolean {
    const mult = this.teamOf(team)?.perks.throwMultiplier ?? 1;
    this.visual.push({ kind: 'explosion', x, y, r: def.radius });
    this.visual.push({ kind: 'shake', power: def.radius / 10 });
    this.blastTerrain(x, y, def.radius, def.damage, { owner, team, shotId, originX });
    const reach = def.radius + UNIT_HALF_W + 8;
    const actor = this.units.find((u) => u.playerId === owner && u.team === team) ?? null;
    let anyEnemy = false;
    for (const u of this.units) {
      if (!u.alive) continue;
      const cx = u.x;
      const cy = u.y - UNIT_H / 2;
      const dx = cx - x;
      const dy = cy - y;
      const d = Math.hypot(dx, dy);
      if (d > reach) continue;
      const falloff = clamp(1 - d / reach, 0.2, 1);
      const isDirect = u === direct;
      // A bolt has no blast: it hurts only what it hits.
      if (def.radius === 0 && !isDirect) continue;
      const headshot = isDirect && y < u.y - (UNIT_H - HEAD_ZONE);
      let dmg = Math.round(def.damage * (isDirect ? 1 : falloff));
      if (headshot) dmg = Math.round(dmg * 1.25);
      const k = def.knockback * falloff * mult * throwFactor + (isDirect ? 2 : 0);
      const nx = d < 1 ? 0 : dx / d;
      const ny = d < 1 ? -1 : dy / d;
      this.knock(u, nx * k, ny * k - 3, true);
      if (u.team !== team) anyEnemy = true;
      this.damage(u, dmg, actor, shotId, weapon, headshot, isDirect, Math.abs(x - originX) / this.level.width, owner, team);
    }
    // Blasts set off mines nearby, one after another.
    for (const m of this.mines) {
      if (m.fuse >= 0) continue;
      if (Math.hypot(m.x - x, m.y - y) <= def.radius + 24) {
        m.armed = true;
        m.fuse = 8;
      }
    }
    return anyEnemy;
  }

  /** A cluster bomb bursts into bomblets that fan out upward and go off a moment later. */
  private splitInto(p: Projectile, n: number): void {
    for (let i = 0; i < n; i++) {
      const spread = (i - (n - 1) / 2) * 2.6;
      this.projectiles.push({
        ...p, id: ++this.projectileCounter, y: p.y - 4, vx: spread + p.vx * 0.15 + (this.rng() - 0.5) * 1.2, vy: -6 - this.rng() * 2.5,
        fuse: BOMBLET.fuseTicks ?? 50, bounces: 0, alive: true, age: 0, homing: false, frag: true,
      });
    }
  }

  // ---- gear that lands ----

  /** A sticky bomb touched something: it stays there (riding a mole if that is what it hit) and ticks. */
  private stickTo(p: Projectile, x: number, y: number, host: Unit | null): void {
    p.x = x;
    p.y = y;
    p.vx = 0;
    p.vy = 0;
    p.stuck = true;
    p.stuckTo = host ? host.id : null;
    p.bounces++;
    this.visual.push({ kind: 'stick', x, y, unitId: host ? host.id : null });
    if (host) this.say(host, ['Get it off!', 'Is that a—', 'Oh no no no.', 'Not fair!']);
  }

  /** A hook bit the ground: the thrower flies to it. */
  private hookBite(p: Projectile, x: number, y: number): void {
    p.alive = false;
    const u = this.units.find((x) => x.id === p.unitId && x.alive);
    this.visual.push({ kind: 'hook', x, y });
    if (u) this.pullTo(u, x, y - 6, 1e6);
  }

  /** A thrown mine stops: drop it onto whatever is below and leave it there. */
  private settleMine(p: Projectile): void {
    p.alive = false;
    const x = clamp(p.x, 4, this.level.width - 4);
    for (let yy = Math.max(0, Math.floor(p.y)); yy < this.level.height; yy++) {
      if (this.level.waterY !== null && yy > this.level.waterY) break;
      if (this.isSolid(x, yy)) {
        this.mines.push({
          id: ++this.mineCounter, x, y: yy - 1, owner: p.owner, team: p.team, shotId: p.shotId, originX: p.originX, armed: false, fuse: -1,
        });
        this.visual.push({ kind: 'build', x, y: yy - 4 });
        return;
      }
    }
    // Nothing under it: lost in the water or the pit.
    if (this.level.waterY !== null) this.visual.push({ kind: 'splash', x, y: this.level.waterY });
  }

  // ---- mines ----

  private updateMines(): void {
    if (this.mines.length === 0) return;
    let exploded = false;
    for (const m of this.mines) {
      // Sitting on something that went away (a blown panel, a crater lip): drop.
      if (!this.isSolid(m.x, m.y + 1)) {
        m.y += 3;
        if (m.y > this.deathY) {
          m.fuse = -2; // gone
          if (this.level.waterY !== null) this.visual.push({ kind: 'splash', x: m.x, y: this.level.waterY });
          continue;
        }
      }
      if (m.fuse >= 0) {
        m.fuse--;
        if (m.fuse < 0) {
          this.detonate(m);
          m.fuse = -2;
          exploded = true;
        }
        continue;
      }
      if (!m.armed) continue;
      for (const u of this.units) {
        if (!u.alive || u.team === m.team || u.pull) continue;
        if (Math.abs(u.x - m.x) <= MINE_TRIGGER && u.y >= m.y - UNIT_H - 12 && u.y <= m.y + 14) {
          m.fuse = MINE_FUSE;
          this.say(u, ['Click.', 'Uh oh.', 'Was that a—', 'Oh no.']);
          break;
        }
      }
    }
    if (exploded || this.mines.some((m) => m.fuse === -2)) this.mines = this.mines.filter((m) => m.fuse !== -2);
  }

  private detonate(m: Mine): void {
    const def = ACTIONS.mine;
    const anyEnemy = this.blast(m.x, m.y - 4, def, 'mine', m.owner, m.team, m.shotId, m.originX, 1.2, null);
    if (anyEnemy) this.shotHitSomething = true;
  }

  private relation(actorPlayer: string, actorTeam: number, target: Unit): Relation {
    if (target.playerId === actorPlayer) return 'self';
    if (target.team === actorTeam) return 'friendly';
    return 'enemy';
  }

  private damage(
    u: Unit, dmg: number, actorUnit: Unit | null, shotId: string, weapon: WeaponId, headshot: boolean, direct: boolean,
    distanceFraction: number, actorPlayer = actorUnit?.playerId ?? 'ai', actorTeam = actorUnit?.team ?? -1,
  ): void {
    if (dmg <= 0 || !u.alive) return;
    u.hp -= dmg;
    u.hitFlash = 10;
    u.lastHitShot = shotId;
    u.lastHitBy = actorPlayer;
    u.lastHitTick = this.tick;
    const relation = this.relation(actorPlayer, actorTeam, u);
    this.visual.push({ kind: 'damage', x: u.x, y: u.y - UNIT_H, amount: dmg, team: u.team, headshot });
    if (actorPlayer !== 'ai') {
      this.events.push({ type: 'hit', shotId, actor: actorPlayer, target: u.playerId, damage: dmg, headshot, direct, distanceFraction, weapon, relation });
    }
    if (relation === 'enemy') {
      const payee = this.teamOfPlayer(actorPlayer);
      const fancyDirect = direct && ACTIONS[weapon].kind !== 'melee' && ACTIONS[weapon].kind !== 'hitscan';
      if (payee) {
        if (headshot) payee.coins += coinsFor(BOUNTIES.headshot, this.wealth);
        if (distanceFraction >= 0.6) payee.coins += coinsFor(BOUNTIES.longShot, this.wealth);
        if (fancyDirect) payee.coins += coinsFor(BOUNTIES.directHit, this.wealth);
      }
      if (headshot) this.visual.push({ kind: 'callout', text: 'Headshot!' });
      else if (distanceFraction >= 0.6) this.visual.push({ kind: 'callout', text: 'Long shot!' });
      else if (fancyDirect) this.visual.push({ kind: 'callout', text: 'Direct hit!' });
      if (u.hp > 0 && weapon !== 'boot' && this.rng() < 0.6) this.say(u, OUCH);
    }
    if (u.hp <= 0) this.kill(u, 'damage');
  }

  private say(u: Unit, lines: readonly string[]): void {
    this.visual.push({ kind: 'say', unitId: u.id, text: lines[Math.floor(this.rng() * lines.length)] });
  }

  private kill(u: Unit, cause: 'damage' | 'drown' | 'fall'): void {
    if (!u.alive) return;
    const team = this.teamOf(u.team);
    if (team && team.perks.reviveOnce && !team.revived && !team.isEnemyAi && cause === 'damage') {
      team.revived = true;
      u.hp = 20;
      u.vx = 0;
      u.vy = -4;
      u.ragdoll = false;
      this.visual.push({ kind: 'callout', text: 'Second wind!' });
      return;
    }
    u.alive = false;
    u.hp = 0;
    u.moveDir = 0;
    this.visual.push({ kind: 'death', x: u.x, y: Math.min(u.y, this.deathY), team: u.team, cause });
    if (cause === 'drown') this.visual.push({ kind: 'splash', x: u.x, y: this.level.waterY ?? u.y });
    const recent = u.lastHitShot && this.tick - u.lastHitTick < 900;
    const actor = recent ? u.lastHitBy! : u.playerId;
    const actorTeam = recent ? (this.units.find((x) => x.playerId === actor)?.team ?? -1) : u.team;
    const shotId = recent ? u.lastHitShot! : `env:${this.tick}`;
    const relation = recent ? this.relation(actor, actorTeam, u) : 'self';
    if (actor !== 'ai') this.events.push({ type: 'kill', shotId, actor, target: u.playerId, cause, relation });
    if (relation === 'enemy') {
      const payee = this.teamOfPlayer(actor);
      if (payee) payee.coins += coinsFor(BOUNTIES.kill + (cause === 'damage' ? 0 : BOUNTIES.splash), this.wealth);
    }
    if (relation === 'enemy') {
      if (cause === 'drown') this.visual.push({ kind: 'callout', text: 'Splash!' });
      else if (cause === 'fall') this.visual.push({ kind: 'callout', text: 'Long way down!' });
      const killer = this.units.find((x) => x.alive && x.playerId === actor);
      if (killer && cause !== 'damage') this.say(killer, BYE);
    }
  }

  private updateLastStand(): void {
    for (const team of this.teams) {
      if (team.isEnemyAi || team.lastStand) continue;
      const alive = this.units.filter((u) => u.alive && u.team === team.team);
      if (alive.length === 1 && alive[0].hp < alive[0].maxHp * 0.25) team.lastStand = true;
    }
  }

  // ---------- end ----------

  private checkEnd(): void {
    if (this.ended) return;
    const t = this.turn;
    if (t.phase !== 'between' && t.phase !== 'resolve' && t.phase !== 'live') return;
    if (this.projectiles.some((p) => p.alive) || this.mines.some((m) => m.fuse >= 0)) return;
    if (this.resetPending) return;
    const aliveTeams = this.teams.filter((team) => this.units.some((u) => u.alive && u.team === team.team));
    const stalled = t.round - this.fightStartRound >= MAX_ROUNDS;
    if (isPvpMode(this.settings.mode)) {
      if (aliveTeams.length <= 1 || stalled) this.endFight(stalled ? [] : aliveTeams);
      return;
    }
    const playersAlive = aliveTeams.some((team) => !team.isEnemyAi);
    const enemiesAlive = aliveTeams.some((team) => team.isEnemyAi);
    const finished = aliveTeams.length === 0 || stalled || !playersAlive || (!enemiesAlive && this.wavesLeft <= 1);
    if (!finished) return;
    // Co-op best-of-N: a cleared set of waves (or a wiped crew) is one round; endFight scores it,
    // resets the field with wave one again, and decides the match when a side has enough rounds.
    if (this.settings.mode === 'coop' && t.bestOf > 1) {
      this.endFight(stalled ? [] : aliveTeams);
      return;
    }
    this.ended = true;
    t.phase = 'ended';
    this.winnerTeam = aliveTeams.length === 1 ? aliveTeams[0].team : null;
    this.outcomes = this.players.map((p) => this.outcomeFor(p));
    this.visual.push({ kind: 'callout', text: this.winnerTeam === null ? 'Draw!' : `${this.teamOf(this.winnerTeam)?.name ?? 'Someone'} wins!` });
  }

  private outcomeFor(p: MatchPlayer): PlayerOutcome {
    const mine = this.units.filter((u) => u.playerId === p.id);
    const team = this.teams.find((t) => t.playerIds.includes(p.id));
    const myTeam = isPvpMode(this.settings.mode) ? p.team : 0;
    const result = this.winnerTeam === null ? 'draw' : this.winnerTeam === myTeam ? 'win' : 'loss';
    const outcome: PlayerOutcome = {
      playerId: p.id,
      result,
      unitsStart: mine.length,
      unitsAlive: mine.filter((u) => u.alive).length,
      hpStart: mine.reduce((s, u) => s + u.maxHp, 0),
      hpRemaining: mine.reduce((s, u) => s + Math.max(0, u.hp), 0),
      roundsPlayed: Math.max(1, this.turn.round),
      parRounds: this.settings.parRounds,
      lastStand: team?.lastStand ?? false,
    };
    if (this.settings.mode === 'campaign' && this.campaignLevelId) {
      outcome.campaign = { levelId: this.campaignLevelId, stars: starsForOutcome(outcome) };
    }
    return outcome;
  }

  // ---------- network ----------

  private hostState(): HostState {
    const lastUnitByTeam: Record<string, string> = {};
    for (const [team, unitId] of this.lastUnitByTeam) lastUnitByTeam[team] = unitId;
    return {
      shotCounter: this.shotCounter,
      projectileCounter: this.projectileCounter,
      mineCounter: this.mineCounter,
      settleTicks: this.settleTicks,
      wavesLeft: this.wavesLeft,
      shotHitSomething: this.shotHitSomething,
      lastUnitByTeam,
      teams: this.teams.map((t) => ({ team: t.team, lastStand: t.lastStand, revived: t.revived, homingLeft: t.homingLeft, used: t.used, items: t.items })),
      winnerTeam: this.winnerTeam,
      outcomes: this.outcomes,
      pendingTeamIndex: this.pendingTeamIndex,
      readyPlayers: [...this.readyPlayers],
      buyNext: this.buyNext,
      resetPending: this.resetPending,
      fightStartRound: this.fightStartRound,
      forfeitedTeams: [...this.forfeitedTeams],
      fightFirstTeamIndex: this.fightFirstTeamIndex,
    };
  }

  private baseSnapshot(): Omit<Snapshot, 'carves' | 'visual' | 'events' | 'ghosts'> {
    const ammo: Record<number, Record<WeaponId, number>> = {};
    const coins: Record<number, number> = {};
    const ready: Record<number, boolean> = {};
    const items: Record<number, Record<ItemId, boolean>> = {};
    for (const t of this.teams) {
      ammo[t.team] = packAmmo(t.ammo);
      coins[t.team] = t.coins;
      ready[t.team] = t.ready;
      items[t.team] = t.items;
    }
    return {
      tick: this.tick, units: this.units, projectiles: this.projectiles, turn: this.turn, wind: this.wind,
      ammo, coins, ready, items, boats: this.boats.map(boatSnap), mines: this.mines, catapults: this.catapults, terrainEpoch: this.terrainEpoch,
      ended: this.ended, eventsTotal: this.events.length, host: this.hostState(),
    };
  }

  /** The regular update for replicas. Drains carves, visuals and new events, so call it once per broadcast. */
  snapshot(): Snapshot {
    const carves = this.carves;
    this.carves = [];
    const events = this.events.slice(this.eventsSent);
    this.eventsSent = this.events.length;
    const ghosts = this.ghosts;
    this.ghosts = [];
    return { ...this.baseSnapshot(), carves, visual: this.drainVisual(), events, ghosts };
  }

  /**
   * Everything a client that joins mid-match needs. Drains nothing: carves still pending go out in
   * the next snapshot and re-carving is harmless, and that snapshot's event delta is indexed so the
   * replica appends only what its copy of the log lacks.
   */
  sync(): SimSync {
    return {
      terrain: encodeMask(this.terrain.solid, this.terrain.w, this.terrain.h),
      events: this.events.slice(),
      snapshot: {
        ...this.baseSnapshot(), carves: [], visual: [], events: [],
        ghosts: this.teams.filter((t) => t.lastShot).map((t) => ({ team: t.team, ghost: t.lastShot! })),
      },
    };
  }
}

/** Non-host clients hold one of these and feed it host snapshots. */
export class RemoteView implements ViewState {
  readonly level: LevelDef;
  readonly terrain: Terrain;
  units: Unit[] = [];
  boats: Boat[] = [];
  mines: Mine[] = [];
  catapults: Catapult[] = [];
  private terrainEpoch = 0;
  projectiles: Projectile[] = [];
  turn: TurnState;
  wind = 0;
  teams: SimTeam[] = [];
  ended = false;
  tick = 0;
  /** The last snapshot as the host sent it (positions not eased), so this client can take over hosting. */
  latest: Snapshot | null = null;
  /** The host's event log as replicated so far; the server pays XP from it if we end up hosting. */
  events: MatchEvent[] = [];
  /** True while a full sync is still owed: after joining mid-match, or after a hole in the event log. */
  needsSync = false;
  private visual: VisualEvent[] = [];
  private trails = new Map<string, string>();
  private targets = new Map<string, { x: number; y: number }>();

  constructor(settings: MatchSettings, _seed: number, players: MatchPlayer[], terrain?: Terrain) {
    this.level = resolveLevel(settings.map);
    this.terrain = terrain ?? buildTerrain(this.level);
    this.boats = buildBoats(this.level.boats);
    for (const p of players) this.trails.set(p.id, p.equipped.trail);
    const pvp = isPvpMode(settings.mode);
    const teamIds = pvp ? [...new Set(players.map((p) => p.team))].sort() : [0, 1];
    this.teams = teamIds.map((team, index) => ({
      team,
      name: pvp ? `Team ${team + 1}` : team === 0 ? 'Moles' : 'Raiders',
      playerIds: players.filter((p) => (pvp ? p.team === team : team === 0)).map((p) => p.id),
      ammo: startingAmmo(),
      perks: {},
      homingLeft: 0,
      lastStand: false,
      revived: false,
      isEnemyAi: !pvp && team === 1,
      side: pvp ? Math.min(2, (index % 2 === 0 ? 0 : 1) + (index >= 2 ? 2 : 0)) : index,
      coins: 0,
      ready: false,
      used: zeroAmmo(),
      items: { ghost: false },
      lastShot: null,
    }));
    this.turn = {
      phase: 'between', teamIndex: -1, unitId: null, playerId: null, ticksLeft: 0, turnTicks: settings.turnSeconds * 60,
      round: 0, fight: 1, bestOf: settings.mode === 'campaign' ? 1 : settings.bestOf ?? 3, wins: {},
      action: null, stamina: STAMINA, moved: false, aimAngle: 0.6, aimPower: 0.6, wave: 1,
    };
  }

  /** Applies a host snapshot; `snap` places units exactly instead of easing them there. */
  apply(s: Snapshot, snap = false): void {
    this.tick = s.tick;
    if (snap) {
      this.targets.clear();
      this.units = s.units.map((u) => ({ ...u }));
    } else {
      const byId = new Map(this.units.map((u) => [u.id, u]));
      this.units = s.units.map((nu) => {
        const old = byId.get(nu.id);
        if (old && old.alive && nu.alive && Math.hypot(old.x - nu.x, old.y - nu.y) < 80) {
          this.targets.set(nu.id, { x: nu.x, y: nu.y });
          return { ...nu, x: old.x, y: old.y };
        }
        this.targets.delete(nu.id);
        return nu;
      });
    }
    this.projectiles = s.projectiles;
    this.turn = s.turn;
    this.wind = s.wind;
    if (s.terrainEpoch !== undefined && s.terrainEpoch !== this.terrainEpoch) {
      // The host rebuilt the field for a new fight.
      this.terrainEpoch = s.terrainEpoch;
      this.terrain.setMask(buildTerrain(this.level).solid);
    }
    for (const c of s.carves) this.terrain.apply(c);
    for (const bs of s.boats ?? []) {
      const b = this.boats.find((x) => x.id === bs.id);
      if (b) applyBoatSnap(b, bs);
    }
    this.mines = s.mines ?? [];
    this.catapults = s.catapults ?? [];
    this.visual.push(...s.visual);
    for (const t of this.teams) {
      const a = s.ammo[t.team];
      if (a) t.ammo = unpackAmmo(a);
      if (s.coins) t.coins = s.coins[t.team] ?? t.coins;
      if (s.ready) t.ready = s.ready[t.team] ?? t.ready;
      if (s.items?.[t.team]) t.items = { ...t.items, ...s.items[t.team] };
    }
    for (const g of s.ghosts ?? []) {
      const t = this.teams.find((x) => x.team === g.team);
      if (t) t.lastShot = g.ghost;
    }
    // The delta holds events [eventsTotal - events.length, eventsTotal); append only what we lack.
    const start = s.eventsTotal - s.events.length;
    if (start > this.events.length) this.needsSync = true;
    for (let i = Math.max(this.events.length, start); i < s.eventsTotal; i++) this.events.push(s.events[i - start]);
    this.ended = s.ended;
    this.latest = s;
  }

  /** Adopts the host's whole state: terrain mask, event log and current snapshot. Throws on bad data, before changing anything. */
  applySync(sync: SimSync): void {
    const mask = decodeMask(sync.terrain);
    this.terrain.setMask(mask);
    this.events = sync.events.slice();
    this.apply(sync.snapshot, true);
    this.needsSync = false;
  }

  /** Called every frame: ease units toward their latest known positions. */
  update(): void {
    for (const u of this.units) {
      const t = this.targets.get(u.id);
      if (!t) continue;
      u.x += (t.x - u.x) * 0.35;
      u.y += (t.y - u.y) * 0.35;
    }
  }

  trailFor(playerId: string): string {
    return this.trails.get(playerId) ?? 'trail.none';
  }

  drainVisual(): VisualEvent[] {
    const v = this.visual;
    this.visual = [];
    return v;
  }
}
